/**
 * The editor. "Snow day, early dismiss, delayed start, chapel runs long — that
 * is a PIN, not a git push."
 *
 * It also owns the shape of the school: **bell groups** (a set of rooms that
 * ring together on one grid) are made, renamed and deleted here, and teachers
 * are created inside them with a PIN. Two invariants drive that whole half of
 * the screen:
 *
 *  - A group's id never changes. Renaming edits `Grid.title` only, because the
 *    id is what every teacher record and every marked date points at.
 *  - No teacher may be left pointing at a group that does not exist. Deleting a
 *    group therefore either refuses, or moves its teachers somewhere first.
 *
 * Everything here exists so that Nathan, or whoever is in the office at 6:40am
 * with a phone in one hand and coffee in the other, can change what eleven
 * tablets ring today — and see exactly what they will ring — before anything
 * is published.
 *
 * Three rules shaped the whole screen:
 *
 *  1. The scope of an edit is never implicit. "Just this date" and "every day
 *     from now on" are a single, huge, always-visible switch, they colour the
 *     panel differently, they label every band card, and they are restated in
 *     the publish dialog. Getting that wrong means the wrong bells for the rest
 *     of the year, so it is the loudest thing on the page.
 *  2. Nothing is published unseen. The preview runs the REAL engine
 *     (`resolveDay`) over the edited document and shows it beside what is
 *     published right now.
 *  3. The document is never trusted, here or on the server. This screen warns;
 *     `worker/admin.ts` refuses.
 *
 * Nothing outside this file is modified: the styles ride along in a <style>
 * inside the mounted subtree and leave with it.
 */

import { authHeader } from '../core/auth';
import { resolveDay } from '../core/schedule';
import type { Clock } from '../core/clock';
import type { Store } from '../core/store';
import type {
  Band,
  BandKind,
  DayOverride,
  DayPlan,
  DayStatus,
  Grid,
  GroupId,
  PublishResponse,
  ScheduleDoc,
  Session,
  Teacher,
} from '../shared/types';

export interface AdminOptions {
  session: Session;
  store: Store;
  clock: Clock;
  onExit: () => void;
}

// ------------------------------------------------------------------- styling

const CSS = `
.vad, .vad *, .vad *::before, .vad *::after { box-sizing: border-box; }
.vad {
  --p: var(--valor-parchment, #F5F1E8);
  --navy: var(--valor-navy, #040552);
  --ink: var(--valor-ink, #1A1A2E);
  --gold: var(--valor-gold, #B89758);
  --card: #FFFDF7;
  --line: rgba(4, 5, 82, .16);
  --muted: rgba(26, 26, 46, .62);
  --alarm: #8A1C1C;
  position: fixed; inset: 0; z-index: 50;
  overflow-y: auto; overscroll-behavior: contain; -webkit-overflow-scrolling: touch;
  background: var(--p); color: var(--ink);
  font-family: var(--valor-sans, 'Source Sans 3', ui-sans-serif, system-ui, sans-serif);
  font-size: 16px; line-height: 1.4; text-align: left;
}
/* Only the font is inherited: a button inside the navy bar must keep its own
   colour, not the bar's parchment text. */
.vad button { font: inherit; }
.vad input, .vad select, .vad textarea { font: inherit; color: var(--ink); }

/* ---- head ---- */
.vad-head { position: sticky; top: 0; z-index: 20; }
.vad-top {
  display: flex; align-items: center; gap: 10px; padding: 10px 12px;
  padding-top: calc(10px + env(safe-area-inset-top));
  background: var(--navy); color: var(--p);
  border-bottom: 1px solid rgba(184, 151, 88, .55);
}
.vad-mark { font-family: var(--valor-serif, 'EB Garamond', Georgia, serif); font-size: 19px; letter-spacing: .01em; }
.vad-mark b { font-weight: 600; }
.vad-who { margin-left: auto; font-size: 13px; color: rgba(245, 241, 232, .78); white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis; max-width: 40vw; }
.vad-tabs { display: flex; gap: 2px; padding: 0 8px; background: var(--p); border-bottom: 1px solid var(--line); }
.vad-tab {
  appearance: none; background: none; border: 0; border-bottom: 2px solid transparent;
  padding: 11px 12px; min-height: 44px; font-weight: 600; color: var(--muted); cursor: pointer;
}
.vad-tab[aria-selected="true"] { color: var(--navy); border-bottom-color: var(--gold); }

/* ---- layout ---- */
.vad-main { padding: 12px; display: grid; gap: 12px; grid-template-columns: minmax(0, 1fr); align-items: start; }
@media (min-width: 1080px) {
  .vad-main.two { grid-template-columns: minmax(0, 1fr) 430px; padding: 16px; gap: 16px; }
  .vad-side { position: sticky; top: 104px; }
}
@media (min-width: 1600px) { .vad-main.two { grid-template-columns: minmax(0, 1fr) 520px; } }
.vad-col { display: grid; gap: 12px; min-width: 0; }

/* ---- cards ---- */
.vad-card { background: var(--card); border: 1px solid var(--line); border-radius: 12px; padding: 12px; min-width: 0; }
.vad-card > h2 {
  font-family: var(--valor-serif, 'EB Garamond', Georgia, serif); font-size: 20px; font-weight: 600;
  margin: 0 0 2px; color: var(--navy); letter-spacing: .01em;
}
.vad-sub { margin: 0 0 10px; font-size: 13.5px; color: var(--muted); }
.vad-rule { border: 0; border-top: 1px solid var(--line); margin: 12px 0; }
.vad-row { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.vad-spacer { flex: 1 1 auto; }

/* ---- buttons ---- */
.vad-btn {
  appearance: none; min-height: 44px; padding: 0 14px; border-radius: 9px;
  border: 1px solid var(--line); background: #fff; color: var(--navy);
  font-weight: 600; cursor: pointer; white-space: nowrap;
}
.vad-btn:hover:not(:disabled) { border-color: rgba(4, 5, 82, .4); }
.vad-btn:disabled { opacity: .4; cursor: not-allowed; }
.vad-btn.primary { background: var(--navy); border-color: var(--navy); color: var(--p); }
.vad-btn.danger { color: var(--alarm); border-color: rgba(138, 28, 28, .35); }
.vad-btn.small { min-height: 36px; padding: 0 10px; font-size: 14px; }
.vad-btn.ghost { background: transparent; border-color: transparent; color: var(--navy); }
.vad-icon {
  appearance: none; width: 40px; height: 40px; border-radius: 9px; border: 1px solid var(--line);
  background: #fff; color: var(--navy); font-size: 16px; line-height: 1; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
}
.vad-icon:disabled { opacity: .3; cursor: not-allowed; }
.vad-icon.danger { color: var(--alarm); }

/* ---- inputs ---- */
.vad-in {
  min-height: 44px; padding: 8px 10px; border-radius: 9px; border: 1px solid var(--line);
  background: #fff; width: 100%; min-width: 0;
}
.vad-in:focus-visible, .vad-btn:focus-visible, .vad-icon:focus-visible, .vad-tab:focus-visible {
  outline: 2px solid var(--gold); outline-offset: 1px;
}
.vad-time { width: 5.6rem; text-align: center; font-variant-numeric: tabular-nums; letter-spacing: .01em; }
.vad-time.bad { border-color: var(--alarm); }
.vad-lab { display: block; font-size: 12.5px; font-weight: 600; color: var(--muted); margin-bottom: 3px;
  text-transform: uppercase; letter-spacing: .06em; }

/* ---- the scope switch: the loudest thing on the page ---- */
.vad-scope { display: grid; grid-template-columns: 1fr; gap: 8px; }
@media (min-width: 520px) { .vad-scope { grid-template-columns: 1fr 1fr; } }
.vad-choice {
  appearance: none; text-align: left; cursor: pointer; padding: 12px; border-radius: 11px;
  border: 1px solid var(--line); background: #fff; color: var(--ink); min-height: 66px;
}
.vad-choice b { display: block; font-size: 16.5px; color: var(--navy); }
.vad-choice span { display: block; font-size: 13px; color: var(--muted); margin-top: 2px; }
.vad-choice[aria-pressed="true"] { background: var(--navy); border-color: var(--navy); box-shadow: 0 0 0 1px var(--gold); }
.vad-choice[aria-pressed="true"] b, .vad-choice[aria-pressed="true"] span { color: var(--p); }
.vad-banner {
  margin-top: 10px; padding: 10px 12px; border-radius: 9px; font-size: 14px;
  border: 1px solid var(--line); border-left: 3px solid var(--navy); background: rgba(4, 5, 82, .045);
}
.vad-banner.forever { border-left-color: var(--gold); background: rgba(184, 151, 88, .09); }
.vad-banner strong { text-transform: uppercase; letter-spacing: .05em; font-size: 13px; }

/* ---- band rows ---- */
.vad-bands { display: grid; gap: 8px; }
.vad-band { border: 1px solid var(--line); border-left: 2px solid transparent; border-radius: 10px;
  background: #fff; padding: 9px 10px; display: grid; gap: 8px; }
.vad-band.changed { border-left-color: var(--gold); }
.vad-band.bad { border-color: rgba(138, 28, 28, .5); }
.vad-band-top { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.vad-band-name { flex: 1 1 9rem; min-width: 7rem; max-width: 34rem; }
.vad-dash { color: var(--muted); }
.vad-acts { display: flex; gap: 5px; margin-left: auto; }
.vad-meta { font-size: 12.5px; color: var(--muted); display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
.vad-was { color: var(--muted); font-size: 12.5px; }
.vad-err { color: var(--alarm); font-size: 13px; font-weight: 600; }
.vad-timehint { flex: 1 1 100%; }
.vad-more { display: grid; gap: 8px; padding-top: 6px; border-top: 1px dashed var(--line); }
.vad-flags { display: flex; gap: 6px; flex-wrap: wrap; }
.vad-chip {
  appearance: none; cursor: pointer; border-radius: 999px; border: 1px solid var(--line);
  background: #fff; color: var(--muted); padding: 7px 11px; min-height: 36px; font-size: 13.5px; font-weight: 600;
}
.vad-chip[aria-pressed="true"] { background: var(--navy); border-color: var(--navy); color: var(--p); }
.vad-chip[aria-pressed="true"].hair { background: #fff; color: var(--navy); border-color: var(--gold); }

/* ---- preview ---- */
.vad-cols { display: grid; grid-template-columns: 1fr; gap: 10px; }
@media (min-width: 560px) { .vad-cols { grid-template-columns: 1fr 1fr; } }
.vad-pane { border: 1px solid var(--line); border-radius: 10px; background: #fff; padding: 9px; min-width: 0; }
.vad-pane.draft { order: -1; border-color: rgba(4, 5, 82, .32); }
@media (min-width: 560px) { .vad-pane.draft { order: 0; } }
.vad-pane h3 { margin: 0 0 2px; font-size: 12.5px; text-transform: uppercase; letter-spacing: .06em; color: var(--muted); }
.vad-pane .vad-stat { font-size: 13px; color: var(--muted); margin: 0 0 7px; }
.vad-ev { display: flex; gap: 7px; align-items: baseline; font-size: 14px; padding: 3px 0 3px 6px;
  border-left: 2px solid transparent; }
.vad-ev.new { border-left-color: var(--gold); }
.vad-ev time { font-variant-numeric: tabular-nums; min-width: 5.4rem; font-weight: 600; }
.vad-ev.warn time { font-weight: 400; color: var(--muted); }
.vad-ev .lbl { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.vad-ev .glyph { margin-left: auto; font-size: 12px; color: var(--muted); }
.vad-empty { padding: 14px 6px; text-align: center; color: var(--alarm); font-weight: 600; }
.vad-warn { margin-top: 8px; display: grid; gap: 5px; }
.vad-warn div { font-size: 13.5px; padding: 7px 9px; border-radius: 8px;
  border: 1px solid rgba(138, 28, 28, .3); border-left: 3px solid var(--alarm); background: rgba(138, 28, 28, .05); }
.vad-warn div.soft { border-color: var(--line); border-left-color: var(--gold); background: rgba(184, 151, 88, .08); color: var(--ink); }
.vad-detail summary { cursor: pointer; font-size: 13px; color: var(--muted); padding: 6px 0; }
.vad-span { display: flex; gap: 8px; font-size: 13px; padding: 2px 0; }
.vad-span time { font-variant-numeric: tabular-nums; min-width: 8.2rem; color: var(--muted); }

/* ---- teachers and bell groups ---- */
.vad-teacher, .vad-item { border: 1px solid var(--line); border-radius: 10px; background: #fff; padding: 10px; display: grid; gap: 8px; }
.vad-teacher + .vad-teacher, .vad-item + .vad-item { margin-top: 8px; }
.vad-grid2 { display: grid; grid-template-columns: 1fr; gap: 8px; }
@media (min-width: 620px) { .vad-grid2 { grid-template-columns: 1fr 1fr; } }
.vad-pin { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
.vad-pin .vad-in { width: 7.5rem; flex: 0 0 auto; }
.vad-note { font-size: 13px; color: var(--muted); }
.vad-note.ok { color: #1E5B2E; font-weight: 600; }
.vad-note.bad { color: var(--alarm); font-weight: 600; }
/* Waiting on a publish: not done, not wrong. Gold hairline, never a fill. */
.vad-note.wait { color: var(--navy); font-weight: 600; border-left: 2px solid var(--gold);
  padding-left: 7px; flex: 1 1 12rem; }

/* ---- bell groups ---- */
.vad-groupname { font-family: var(--valor-serif, 'EB Garamond', Georgia, serif); font-size: 19px;
  color: var(--navy); font-weight: 600; }
.vad-id { font-size: 12px; color: var(--muted); font-variant-numeric: tabular-nums;
  border: 1px solid var(--line); border-radius: 999px; padding: 2px 8px; white-space: nowrap; }
/* A dropdown next to a label, not a full-width block. */
.vad-pick { width: auto; min-width: 9rem; max-width: 100%; flex: 0 1 auto; }
.vad-sheet select.vad-in, .vad-sheet input.vad-in { width: 100%; }

/* ---- publish bar ---- */
.vad-bar {
  position: sticky; bottom: 0; z-index: 15; display: flex; gap: 10px; align-items: center;
  padding: 10px 12px; padding-bottom: calc(10px + env(safe-area-inset-bottom));
  background: var(--card); border-top: 1px solid var(--line);
  box-shadow: 0 -6px 18px rgba(4, 5, 82, .07);
}
.vad-bar .count { font-size: 13.5px; color: var(--muted); min-width: 0; }
.vad-bar .count b { color: var(--navy); font-size: 15px; display: block; }
.vad-bar .count > span { display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* Narrow: the band name takes its own line so start–end stay a pair. */
@media (max-width: 560px) {
  .vad-band-name { flex: 1 1 100%; }
  .vad-time { width: 5rem; }
  .vad-acts { margin-left: auto; }
  .vad-icon { width: 44px; height: 44px; }
}

/* Phone. The office does this one-handed in a coat, in the car park. */
@media (max-width: 480px) {
  .vad-mark { font-size: 17px; white-space: nowrap; }
  .vad-who { display: none; }
  .vad-main { padding: 10px; }
  .vad-bar .count b { font-size: 14px; white-space: nowrap; }
  .vad-bar { gap: 8px; padding: 8px 10px; }
  .vad-btn { padding: 0 11px; }
}
.vad-flash { font-size: 14px; font-weight: 600; }
.vad-flash.ok { color: #1E5B2E; }
.vad-flash.bad { color: var(--alarm); }

/* ---- sheets ---- */
.vad-overlay { position: fixed; inset: 0; z-index: 60; background: rgba(4, 5, 82, .42);
  display: flex; align-items: flex-end; justify-content: center; padding: 0; }
@media (min-width: 620px) { .vad-overlay { align-items: center; padding: 20px; } }
.vad-sheet {
  background: var(--p); border: 1px solid var(--line); border-radius: 14px 14px 0 0;
  width: 100%; max-width: 560px; max-height: 92vh; overflow-y: auto; padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
  box-shadow: 0 -10px 40px rgba(4, 5, 82, .3);
}
@media (min-width: 620px) { .vad-sheet { border-radius: 14px; box-shadow: 0 20px 60px rgba(4, 5, 82, .35); } }
.vad-sheet h2 { font-family: var(--valor-serif, 'EB Garamond', Georgia, serif); font-size: 23px; margin: 0 0 4px; color: var(--navy); }
.vad-list { margin: 10px 0; padding: 0; list-style: none; display: grid; gap: 6px; }
.vad-list li { font-size: 14px; padding: 8px 10px; border-radius: 8px; background: #fff;
  border: 1px solid var(--line); border-left: 2px solid var(--gold); }
.vad-confirm { display: flex; gap: 9px; align-items: flex-start; padding: 10px; border-radius: 9px;
  border: 1px solid rgba(138, 28, 28, .35); background: rgba(138, 28, 28, .05); margin: 10px 0; font-size: 14px; }
.vad-confirm input { width: 22px; height: 22px; flex: 0 0 auto; margin: 0; }
.vad-sheet-acts { display: flex; gap: 8px; justify-content: flex-end; flex-wrap: wrap; margin-top: 14px; }
.vad-sheet-acts .vad-btn { flex: 1 1 auto; }
@media (min-width: 620px) { .vad-sheet-acts .vad-btn { flex: 0 0 auto; } }
`;

// -------------------------------------------------------------- dom helpers

type Kid = Node | string | number | null | undefined | false | Kid[];

function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, unknown> = {},
  ...kids: Kid[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === null || value === false) continue;
    if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    } else if (key === 'class') {
      node.className = String(value);
    } else if (key === 'for' || key.includes('-') || key.startsWith('aria')) {
      node.setAttribute(key, String(value));
    } else {
      (node as unknown as Record<string, unknown>)[key] = value;
    }
  }
  append(node, kids);
  return node;
}

/**
 * Use this rather than `node.append(...)`. The project compiles with
 * `@cloudflare/workers-types` in scope, which merges an HTMLRewriter `Element`
 * over the DOM one and leaves `append()` typed as the rewriter's
 * `append(string)`. `appendChild` is untouched, so we go through it.
 */
function add(node: Node, ...kids: Kid[]): void {
  append(node, kids);
}

function append(node: Node, kids: Kid[]): void {
  for (const kid of kids) {
    if (kid === null || kid === undefined || kid === false) continue;
    if (Array.isArray(kid)) append(node, kid);
    else node.appendChild(typeof kid === 'object' ? kid : document.createTextNode(String(kid)));
  }
}

// ------------------------------------------------------------- time helpers

/** 490 -> '8:10'. Same shape as the printed sheet: no am/pm, no zero padding. */
function fmt12(m: number | null | undefined): string {
  if (m === null || m === undefined || !Number.isFinite(m)) return '';
  const mins = Math.trunc(m);
  const h24 = Math.floor(mins / 60);
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h}:${String(mins % 60).padStart(2, '0')}`;
}

/** For the bell list, where 'is that 8:10 in the morning?' must not be a question. */
function fmt12ap(m: number | null | undefined): string {
  if (m === null || m === undefined || !Number.isFinite(m)) return '';
  return `${fmt12(m)} ${Math.floor(m / 60) < 12 ? 'am' : 'pm'}`;
}

function fmtSpan(b: Band): string {
  return b.end === null ? fmt12(b.start) : `${fmt12(b.start)} – ${fmt12(b.end)}`;
}

/**
 * Forgiving on purpose. The office types '830', '8:30', '08:30', '8:30am' and
 * sometimes just '3' — all of them mean a time on a school day.
 *
 * A bare hour is read the way the printed schedule reads: the day runs 7:45am
 * to 3:00pm, so 7–11 is morning, 12 is noon, 1–6 is afternoon. 13–23 is taken
 * as 24-hour for anyone who types it that way.
 */
export function parseTime(raw: string): number | null {
  const text = raw.trim().toLowerCase().replace(/[.\s]/g, '');
  if (!text) return null;

  let meridiem: 'am' | 'pm' | null = null;
  let body = text;
  const mMatch = body.match(/(am|pm|a|p)$/);
  if (mMatch) {
    meridiem = mMatch[0].startsWith('a') ? 'am' : 'pm';
    body = body.slice(0, -mMatch[0].length);
  }

  let hours: number;
  let minutes: number;
  if (body.includes(':')) {
    const [hh, mm = '0'] = body.split(':');
    if (!/^\d{1,2}$/.test(hh ?? '') || !/^\d{1,2}$/.test(mm)) return null;
    hours = Number(hh);
    minutes = Number(mm);
  } else if (/^\d{1,4}$/.test(body)) {
    if (body.length <= 2) {
      hours = Number(body);
      minutes = 0;
    } else {
      hours = Number(body.slice(0, body.length - 2));
      minutes = Number(body.slice(-2));
    }
  } else {
    return null;
  }

  if (minutes > 59) return null;

  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    if (meridiem === 'am') hours = hours === 12 ? 0 : hours;
    else hours = hours === 12 ? 12 : hours + 12;
  } else if (hours >= 1 && hours <= 6) {
    hours += 12; // afternoon: '1:10' is 1:10pm
  } else if (hours > 23) {
    return null;
  }

  const total = hours * 60 + minutes;
  return total >= 0 && total <= 1439 ? total : null;
}

// ------------------------------------------------------------- date helpers

const TZ = 'America/Chicago';
const ISO_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const LONG_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  weekday: 'long',
  month: 'long',
  day: 'numeric',
});
const SHORT_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});

function todayISO(nowMs: number): string {
  return ISO_FMT.format(new Date(nowMs));
}

function atNoon(iso: string): Date {
  return new Date(`${iso}T12:00:00Z`);
}

function addDays(iso: string, days: number): string {
  const d = atNoon(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function longDate(iso: string): string {
  return LONG_FMT.format(atNoon(iso));
}

function shortDate(iso: string): string {
  return SHORT_FMT.format(atNoon(iso));
}

// ----------------------------------------------------------------- plumbing

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** A 401 here means the token died, not that the schedule was wrong. Say so. */
const SIGNED_OUT =
  'Your admin sign-in has expired. Exit to the kiosk and sign in with your PIN again — your edits stay on this screen.';

const KINDS: BandKind[] = ['arrival', 'chapel', 'class', 'break', 'lunch', 'dismissal'];

/**
 * Leads offered for the "five minutes left" bell. A short list rather than a
 * free number: the office is picking how long a class needs to pack up, not
 * setting a time, and every one of these is a whole answer to that question.
 * Five is what Art and Choir asked for.
 */
const BEFORE_END_CHOICES: (number | null)[] = [null, 3, 5, 10];

// -------------------------------------------------------------- bell groups

/**
 * The groups a document has, in published order. There is no fixed list
 * anywhere in this file: the office owns it, so everything reads it from the
 * document being edited.
 */
function groupIds(doc: ScheduleDoc): GroupId[] {
  return Object.keys(doc.grids);
}

function gridOf(doc: ScheduleDoc, id: GroupId): Grid | undefined {
  return doc.grids[id];
}

/** What a person calls this group. Falls back to the id so nothing reads blank. */
function groupName(doc: ScheduleDoc, id: GroupId): string {
  return doc.grids[id]?.title || id;
}

function groupBands(doc: ScheduleDoc, id: GroupId): Band[] {
  return doc.grids[id]?.bands ?? [];
}

/**
 * 'Preschool' -> 'preschool'. The id must match what `worker/admin.ts` accepts:
 * lowercase letters, numbers and hyphens, starting with a letter or number.
 */
function slugify(name: string): string {
  const slug = name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/, '');
  return slug;
}

/**
 * A new id may never land on one already in use — a collision would silently
 * merge two schedules, and every teacher in the older group would start ringing
 * the newer one's bells.
 */
function freeId(base: string, taken: Iterable<string>, fallback: string): string {
  const used = new Set(taken);
  const root = (base || fallback).slice(0, 36).replace(/-+$/, '') || fallback;
  if (!used.has(root)) return root;
  for (let n = 2; n < 200; n++) {
    const candidate = `${root}-${n}`;
    if (!used.has(candidate)) return candidate;
  }
  return `${root}-${Date.now().toString(36)}`;
}

// ================================================================== the mount

export function mountAdmin(root: HTMLElement, opts: AdminOptions): () => void {
  const { session, store, clock, onExit } = opts;

  // ---- state
  let draft: ScheduleDoc = clone(store.doc);
  let baseline: ScheduleDoc = clone(store.doc);
  let scope: 'date' | 'default' = 'date';
  let date = todayISO(clock.now());
  /** The bell group being edited. Always one that exists in the draft. */
  let group: GroupId = groupIds(draft)[0] ?? '';
  let tab: 'schedule' | 'groups' | 'teachers' = 'schedule';
  let previewDate = date;
  let previewGroup: GroupId = group;
  let openBand: string | null = null;
  let flash: { kind: 'ok' | 'bad'; text: string } | null = null;
  const pinDraft = new Map<string, string>();
  /** 'wait' is neither done nor wrong: it is "published first, then this PIN". */
  const pinNote = new Map<string, { kind: 'ok' | 'bad' | 'wait'; text: string }>();
  /** Which slots already have a PIN, straight from the worker. */
  const hasPin = new Map<string, boolean>();
  /**
   * PINs typed for teachers who are not on the tablets yet. A credential must
   * never point at somebody the published document has never heard of, so these
   * wait here and are sent the moment the publish that creates them succeeds.
   */
  const pinAfterPublish = new Map<string, string>();

  const dirty = (): boolean => !same(draft, baseline);

  /**
   * Keep the selected group real. A group can vanish under the editor — deleted
   * here, or replaced by somebody else's publish — and every card below indexes
   * `draft.grids` by it.
   */
  function clampGroup(): void {
    const ids = groupIds(draft);
    if (!ids.includes(group)) group = ids[0] ?? '';
    if (!ids.includes(previewGroup)) previewGroup = group;
  }

  // ---- shell
  const style = h('style', {}, CSS);
  const shell = h('div', { class: 'vad' });
  const topRow = h('div', { class: 'vad-top' });
  const tabsRow = h('div', { class: 'vad-tabs' });
  const head = h('div', { class: 'vad-head' }, topRow, tabsRow);
  const editorCol = h('div', { class: 'vad-col' });
  const sideCol = h('aside', { class: 'vad-col vad-side' });
  const main = h('div', { class: 'vad-main two' }, editorCol, sideCol);
  const bar = h('div', { class: 'vad-bar' });
  add(shell, style, head, main, bar);
  root.replaceChildren(shell);

  const guard = (event: BeforeUnloadEvent): void => {
    if (!dirty()) return;
    event.preventDefault();
    event.returnValue = '';
  };
  window.addEventListener('beforeunload', guard);

  // ------------------------------------------------------------- doc access

  function ensureOverride(iso: string): DayOverride {
    let ov = draft.overrides[iso];
    if (!ov) {
      ov = { date: iso, status: 'normal' };
      draft.overrides[iso] = ov;
    }
    return ov;
  }

  /** Drop an override that no longer says anything, so the doc stays honest. */
  function tidyOverride(iso: string): void {
    const ov = draft.overrides[iso];
    if (!ov) return;
    if (ov.grids && !Object.keys(ov.grids).length) delete ov.grids;
    if (ov.status === 'normal' && !ov.grids && !ov.note) delete draft.overrides[iso];
  }

  /** What the editor is currently showing for a bell group. */
  function shownBands(id: GroupId): Band[] {
    if (scope === 'default') return groupBands(draft, id);
    return draft.overrides[date]?.grids?.[id] ?? groupBands(draft, id);
  }

  /** True when this date already carries its own bells for this bell group. */
  function hasDateBands(id: GroupId): boolean {
    return Boolean(draft.overrides[date]?.grids?.[id]);
  }

  /**
   * The array an edit should be written into. In date scope the first touch
   * clones the everyday bells onto this one date — so an edit made under the
   * "just this date" banner can never leak into the rest of the year.
   */
  function editableBands(id: GroupId): Band[] {
    const grid = gridOf(draft, id);
    if (!grid) return [];
    if (scope === 'default') return grid.bands;
    const ov = ensureOverride(date);
    if (!ov.grids) ov.grids = {};
    if (!ov.grids[id]) ov.grids[id] = clone(grid.bands);
    return ov.grids[id]!;
  }

  function bandsPublishedFor(id: GroupId): Band[] {
    if (scope === 'default') return groupBands(baseline, id);
    return baseline.overrides[date]?.grids?.[id] ?? groupBands(baseline, id);
  }

  /**
   * Re-render on the next turn of the loop, never inside the gesture that
   * caused it.
   *
   * A text field commits on `change`, which fires on blur — and blur happens on
   * the mousedown of whatever you tapped NEXT. Re-rendering there rips out the
   * node the browser was in the middle of focusing, so the second tap lands on
   * nothing and the office types a PIN into a field that is not there any more.
   * Deferring by one task lets the tap finish; the focus key is then captured
   * from the field the user actually moved to.
   */
  let renderQueued = false;
  function scheduleRender(): void {
    if (renderQueued) return;
    renderQueued = true;
    setTimeout(() => {
      renderQueued = false;
      render();
    }, 0);
  }

  function changed(): void {
    flash = null;
    scheduleRender();
  }

  // -------------------------------------------------------------- structure

  /** The same ordering rules the worker enforces, so nothing is a surprise. */
  function problems(bands: Band[]): Map<number, string> {
    const out = new Map<number, string>();
    bands.forEach((b, i) => {
      if (b.end !== null && b.end <= b.start) {
        out.set(i, `Ends at ${fmt12(b.end)}, which is not after ${fmt12(b.start)}.`);
        return;
      }
      if (b.end === null && i !== bands.length - 1) {
        out.set(i, 'Only the last band of the day may have no end time.');
        return;
      }
      const prev = bands[i - 1];
      if (!prev) return;
      if (b.start <= prev.start) {
        out.set(i, `Starts at or before “${prev.label}” above it. Bands run in time order.`);
      } else if (prev.end !== null && prev.end > b.start) {
        out.set(i, `Overlaps “${prev.label}”, which runs to ${fmt12(prev.end)}.`);
      }
    });
    return out;
  }

  function anyProblems(doc: ScheduleDoc): string[] {
    const lines: string[] = [];
    const ids = groupIds(doc);
    if (!ids.length) {
      lines.push('There are no bell groups left. Every school needs at least one.');
    }
    for (const id of ids) {
      const bands = groupBands(doc, id);
      if (!bands.length) {
        lines.push(`${groupName(doc, id)} has no bands. It would ring nothing all day.`);
      }
      if (!gridOf(doc, id)?.title.trim()) {
        lines.push(`The bell group “${id}” has no name.`);
      }
      for (const [i, msg] of problems(bands)) {
        lines.push(`${groupName(doc, id)}, “${bands[i]?.label ?? ''}”: ${msg}`);
      }
    }
    for (const teacher of doc.teachers) {
      if (!gridOf(doc, teacher.wing)) {
        lines.push(
          `${teacher.name || teacher.roomLabel || teacher.id} is in a bell group that no longer exists — that tablet would ring nothing.`,
        );
      }
    }
    for (const [iso, ov] of Object.entries(doc.overrides)) {
      for (const [id, bands] of Object.entries(ov.grids ?? {})) {
        if (!bands) continue;
        for (const [i, msg] of problems(bands)) {
          lines.push(`${shortDate(iso)} · ${groupName(doc, id)}, “${bands[i]?.label ?? ''}”: ${msg}`);
        }
      }
      if (ov.status === 'delayed' && !ov.delayMinutes) {
        lines.push(`${shortDate(iso)}: a delayed start needs a number of minutes.`);
      }
      if (ov.status === 'early' && ov.dismissAt === undefined) {
        lines.push(`${shortDate(iso)}: an early dismissal needs a dismissal time.`);
      }
    }
    return lines;
  }

  // ---------------------------------------------------------------- preview

  interface Preview {
    plan: DayPlan | null;
    error: string | null;
    /** The group is not in this document at all — brand new, or just deleted. */
    absent: boolean;
  }

  function planOf(doc: ScheduleDoc, iso: string, id: GroupId): Preview {
    // A group that is not in this document has no day to resolve. Say that
    // plainly rather than letting the engine throw at it.
    if (!gridOf(doc, id)) return { plan: null, error: null, absent: true };
    try {
      return { plan: resolveDay(doc, iso, id), error: null, absent: false };
    } catch (err) {
      return { plan: null, error: err instanceof Error ? err.message : String(err), absent: false };
    }
  }

  function warningsFor(plan: DayPlan | null): { text: string; soft: boolean }[] {
    if (!plan) return [];
    const out: { text: string; soft: boolean }[] = [];
    if (!plan.isSchoolDay) {
      out.push({ text: 'Not a school day — the tablets stay quiet.', soft: true });
      return out;
    }
    if (!plan.events.length) {
      out.push({ text: 'This bell group rings NOTHING on this day.', soft: false });
    }
    plan.bands.forEach((b, i) => {
      const prev = plan.bands[i - 1];
      if (prev && prev.end !== null && prev.end > b.start) {
        out.push({ text: `“${prev.label}” overlaps “${b.label}”.`, soft: false });
      }
      if (b.end !== null && b.end - b.start < 5) {
        out.push({ text: `“${b.label}” is only ${b.end - b.start} minutes long.`, soft: true });
      }
    });
    const minutes = new Map<number, string[]>();
    for (const ev of plan.events) {
      if (ev.kind !== 'bell') continue;
      const list = minutes.get(ev.at) ?? [];
      list.push(ev.label);
      minutes.set(ev.at, list);
    }
    for (const [at, labels] of minutes) {
      if (labels.length > 1) {
        out.push({ text: `Two bells land on ${fmt12ap(at)} (${labels.join(', ')}).`, soft: true });
      }
    }
    return out;
  }

  function renderPane(
    title: string,
    view: Preview,
    other: DayPlan | null,
    cls: string,
  ): HTMLElement {
    const { plan, error } = view;
    const pane = h('div', { class: `vad-pane ${cls}` }, h('h3', {}, title));
    if (view.absent) {
      add(
        pane,
        h(
          'div',
          { class: 'vad-empty' },
          cls === 'live'
            ? 'This bell group is not published yet.'
            : 'This bell group is not in the schedule any more.',
        ),
      );
      return pane;
    }
    if (error) {
      add(pane, h('div', { class: 'vad-empty' }, `Preview engine error: ${error}`));
      return pane;
    }
    if (!plan) {
      add(pane, h('div', { class: 'vad-empty' }, 'No plan.'));
      return pane;
    }

    const bells = plan.events.filter((e) => e.kind === 'bell').length;
    const songs = plan.events.length - bells;
    const status =
      plan.status === 'closed'
        ? 'Closed'
        : plan.status === 'delayed'
          ? 'Delayed start'
          : plan.status === 'early'
            ? 'Early dismissal'
            : plan.isSchoolDay
              ? 'Normal day'
              : 'No school';
    add(pane, 
      h(
        'p',
        { class: 'vad-stat' },
        `${status} · ${bells} bell${bells === 1 ? '' : 's'} · ${songs} song${songs === 1 ? '' : 's'}`,
        plan.note ? ` · ${plan.note}` : '',
      ),
    );

    if (!plan.events.length) {
      add(pane, h('div', { class: 'vad-empty' }, plan.isSchoolDay ? 'Rings nothing' : 'No school'));
    } else {
      const otherKeys = new Set((other?.events ?? []).map((e) => `${e.kind}@${e.at}:${e.label}`));
      for (const ev of plan.events) {
        const isNew = other !== null && !otherKeys.has(`${ev.kind}@${ev.at}:${ev.label}`);
        add(pane, 
          h(
            'div',
            { class: `vad-ev ${ev.kind === 'warn' ? 'warn' : ''} ${isNew ? 'new' : ''}` },
            h('time', {}, fmt12ap(ev.at)),
            h('span', { class: 'lbl' }, ev.label),
            h('span', { class: 'glyph' }, ev.kind === 'warn' ? '♪ 1-min' : 'bell'),
          ),
        );
      }
    }

    const spans = h('details', { class: 'vad-detail' }, h('summary', {}, `Bands (${plan.bands.length})`));
    for (const b of plan.bands) {
      add(spans, h('div', { class: 'vad-span' }, h('time', {}, fmtSpan(b)), h('span', {}, b.label)));
    }
    add(pane, spans);

    const warns = warningsFor(plan);
    if (warns.length) {
      const box = h('div', { class: 'vad-warn' });
      for (const w of warns) add(box, h('div', { class: w.soft ? 'soft' : '' }, w.text));
      add(pane, box);
    }
    return pane;
  }

  function renderPreview(): HTMLElement {
    const card = h('div', { class: 'vad-card' });
    add(card, 
      h('h2', {}, 'What this tablet will ring'),
      h('p', { class: 'vad-sub' }, 'The real engine, run over your unpublished edits.'),
    );

    const pick = h(
      'div',
      { class: 'vad-row' },
      segment(
        groupIds(draft).map((id) => ({ value: id, label: groupName(draft, id) })),
        previewGroup,
        (v) => {
          previewGroup = v;
          scheduleRender();
        },
      ),
      h('input', {
        class: 'vad-in',
        type: 'date',
        value: previewDate,
        style: 'width:auto;flex:1 1 9rem',
        'aria-label': 'Preview date',
        onchange: (e: Event) => {
          const v = (e.target as HTMLInputElement).value;
          if (v) previewDate = v;
          scheduleRender();
        },
      }),
    );
    add(card, pick);
    add(card,
      h(
        'p',
        { class: 'vad-sub', style: 'margin:8px 0 6px' },
        `${longDate(previewDate)} · ${groupName(draft, previewGroup)}`,
      ),
    );

    const live = planOf(store.doc, previewDate, previewGroup);
    const next = planOf(draft, previewDate, previewGroup);
    add(card,
      h(
        'div',
        { class: 'vad-cols' },
        renderPane(`Published now · v${store.doc.version}`, live, null, 'live'),
        renderPane('After you publish', next, live.plan, 'draft'),
      ),
    );

    if (!same(live.plan?.events ?? [], next.plan?.events ?? [])) {
      add(card, 
        h(
          'p',
          { class: 'vad-sub', style: 'margin-top:8px' },
          'Gold marks a bell that is new or moved.',
        ),
      );
    }
    return card;
  }

  // ------------------------------------------------------------ small parts

  function segment(
    options: { value: string; label: string; hint?: string }[],
    current: string,
    onPick: (value: string) => void,
  ): HTMLElement {
    const wrap = h('div', { class: 'vad-flags' });
    for (const o of options) {
      add(wrap, 
        h(
          'button',
          {
            class: 'vad-chip',
            type: 'button',
            'aria-pressed': String(o.value === current),
            onclick: () => onPick(o.value),
          },
          o.label,
        ),
      );
    }
    return wrap;
  }

  function timeInput(
    value: number | null,
    fkey: string,
    onCommit: (minutes: number | null) => void,
    allowEmpty: boolean,
  ): HTMLInputElement {
    const hintKey = fkey.replace(/[^a-zA-Z0-9]+/g, '-');

    /**
     * Say what went wrong where they can see it. A `title` tooltip is invisible
     * on the phone this is most often used from, and a silently rejected time
     * would leave the office believing they had moved a bell.
     */
    const showBad = (text: string): void => {
      input.classList.add('bad');
      const parent = input.parentElement;
      if (!parent) return;
      let hint = parent.querySelector<HTMLElement>(`[data-hintfor="${hintKey}"]`);
      if (!hint) {
        hint = h('span', { class: 'vad-err vad-timehint', 'data-hintfor': hintKey });
        parent.appendChild(hint);
      }
      hint.textContent = text;
    };
    const clearBad = (): void => {
      input.classList.remove('bad');
      input.parentElement?.querySelector(`[data-hintfor="${hintKey}"]`)?.remove();
    };

    const input: HTMLInputElement = h('input', {
      class: 'vad-in vad-time',
      type: 'text',
      inputmode: 'numeric',
      autocomplete: 'off',
      spellcheck: false,
      value: value === null ? '' : fmt12(value),
      placeholder: allowEmpty ? '—' : '8:10',
      'data-fkey': fkey,
      // Small field, one value: typing should replace it, not append to it.
      onfocus: (e: Event) => (e.target as HTMLInputElement).select(),
      onchange: (e: Event) => {
        const el = e.target as HTMLInputElement;
        const text = el.value.trim();
        if (!text) {
          if (allowEmpty) {
            clearBad();
            onCommit(null);
          } else {
            showBad('This band needs a time.');
            el.value = value === null ? '' : fmt12(value);
          }
          return;
        }
        const parsed = parseTime(text);
        if (parsed === null) {
          showBad(`“${text}” is not a time. Try 8:30, 830 or 8:30am.`);
          return;
        }
        clearBad();
        onCommit(parsed);
      },
      onkeydown: (e: KeyboardEvent) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      },
    });
    return input;
  }

  // --------------------------------------------------------- the scope card

  function renderScopeCard(): HTMLElement {
    const card = h('div', { class: 'vad-card' });
    add(card, 
      h('h2', {}, 'What are you changing?'),
      h('p', { class: 'vad-sub' }, 'Pick this first. Everything below obeys it.'),
    );

    const choose = (next: 'date' | 'default'): void => {
      scope = next;
      openBand = null;
      scheduleRender();
    };

    add(card, 
      h(
        'div',
        { class: 'vad-scope' },
        h(
          'button',
          { class: 'vad-choice', type: 'button', 'aria-pressed': String(scope === 'date'), onclick: () => choose('date') },
          h('b', {}, 'Just one date'),
          h('span', {}, 'Snow day, early out, chapel runs long. Tomorrow is normal again.'),
        ),
        h(
          'button',
          {
            class: 'vad-choice',
            type: 'button',
            'aria-pressed': String(scope === 'default'),
            onclick: () => choose('default'),
          },
          h('b', {}, 'The everyday schedule'),
          h('span', {}, 'The permanent bells. Every school day from now on.'),
        ),
      ),
    );

    if (scope === 'date') {
      const today = todayISO(clock.now());
      add(card, 
        h(
          'div',
          { class: 'vad-row', style: 'margin-top:10px' },
          h('button', { class: 'vad-icon', type: 'button', 'aria-label': 'Previous day', onclick: () => { date = addDays(date, -1); previewDate = date; render(); } }, '‹'),
          h('input', {
            class: 'vad-in',
            type: 'date',
            value: date,
            style: 'width:auto;flex:1 1 9rem',
            'aria-label': 'Date being changed',
            onchange: (e: Event) => {
              const v = (e.target as HTMLInputElement).value;
              if (v) { date = v; previewDate = v; }
              scheduleRender();
            },
          }),
          h('button', { class: 'vad-icon', type: 'button', 'aria-label': 'Next day', onclick: () => { date = addDays(date, 1); previewDate = date; render(); } }, '›'),
          h('button', { class: 'vad-btn small', type: 'button', onclick: () => { date = today; previewDate = date; render(); } }, 'Today'),
          h(
            'button',
            {
              class: 'vad-btn small',
              type: 'button',
              onclick: () => {
                const nextDay = nextSchoolDate(draft, addDays(today, 1));
                if (nextDay) { date = nextDay; previewDate = nextDay; }
                scheduleRender();
              },
            },
            'Next school day',
          ),
        ),
        h(
          'div',
          { class: 'vad-banner' },
          h('strong', {}, 'Just this date · '),
          `${longDate(date)}${date === today ? ' (today)' : ''}. Every other day keeps the normal schedule.`,
        ),
      );
    } else {
      add(card, 
        h(
          'div',
          { class: 'vad-banner forever' },
          h('strong', {}, 'Every school day · '),
          'From now on, and for the rest of the year, the bells ring the way you set them here.',
        ),
      );
    }
    return card;
  }

  // ----------------------------------------------------------- the day card

  function renderDayCard(): HTMLElement {
    const card = h('div', { class: 'vad-card' });

    if (scope === 'default') {
      add(card, 
        h('h2', {}, 'Mark a date'),
        h(
          'p',
          { class: 'vad-sub' },
          'Closed, delayed start and early dismissal always belong to one date — they are never permanent.',
        ),
        h(
          'button',
          { class: 'vad-btn', type: 'button', onclick: () => { scope = 'date'; render(); } },
          'Switch to a single date',
        ),
      );
      return card;
    }

    const ov = draft.overrides[date];
    const status: DayStatus = ov?.status ?? 'normal';
    add(card, 
      h('h2', {}, `Mark ${shortDate(date)}`),
      h('p', { class: 'vad-sub' }, longDate(date)),
    );

    const setStatus = (next: DayStatus): void => {
      const target = ensureOverride(date);
      target.status = next;
      if (next === 'delayed' && !target.delayMinutes) target.delayMinutes = 120;
      if (next === 'early' && target.dismissAt === undefined) target.dismissAt = 12 * 60;
      if (next !== 'delayed') delete target.delayMinutes;
      if (next !== 'early') delete target.dismissAt;
      tidyOverride(date);
      changed();
    };

    add(card, 
      segment(
        [
          { value: 'normal', label: 'Normal' },
          { value: 'closed', label: 'Closed' },
          { value: 'delayed', label: 'Delayed start' },
          { value: 'early', label: 'Early dismissal' },
        ],
        status,
        (v) => setStatus(v as DayStatus),
      ),
    );

    if (status === 'delayed') {
      const delay = ov?.delayMinutes ?? 120;
      const firstBell = shownBands(group).find((b) => b.ringStart) ?? shownBands(group)[0];
      const setDelay = (mins: number): void => {
        const target = ensureOverride(date);
        target.delayMinutes = Math.max(1, Math.min(600, Math.round(mins)));
        changed();
      };
      add(card, 
        h(
          'div',
          { class: 'vad-row', style: 'margin-top:10px' },
          h('label', { class: 'vad-lab', for: 'vad-delay', style: 'margin:0' }, 'Everything moves later by'),
          h('input', {
            class: 'vad-in vad-time',
            id: 'vad-delay',
            type: 'number',
            min: '5',
            max: '600',
            step: '5',
            value: String(delay),
            'data-fkey': 'delay',
            onchange: (e: Event) => setDelay(Number((e.target as HTMLInputElement).value)),
          }),
          h('span', { class: 'vad-note' }, 'minutes'),
          ...[60, 90, 120].map((m) =>
            h('button', { class: 'vad-btn small', type: 'button', onclick: () => setDelay(m) }, `${m / 60} hr${m === 60 ? '' : 's'}`),
          ),
        ),
        firstBell
          ? h(
              'p',
              { class: 'vad-note', style: 'margin:6px 0 0' },
              `${groupName(draft, group)}: the ${firstBell.label.toLowerCase()} bell moves ${fmt12(firstBell.start)} → ${fmt12(Math.min(1439, firstBell.start + delay))}.`,
            )
          : null,
      );
    }

    if (status === 'early') {
      const at = ov?.dismissAt ?? 12 * 60;
      const setAt = (mins: number): void => {
        const target = ensureOverride(date);
        target.dismissAt = Math.max(0, Math.min(1439, Math.round(mins)));
        changed();
      };
      add(card, 
        h(
          'div',
          { class: 'vad-row', style: 'margin-top:10px' },
          h('span', { class: 'vad-lab', style: 'margin:0' }, 'The day ends at'),
          timeInput(at, 'dismissAt', (m) => { if (m !== null) setAt(m); }, false),
          ...[11 * 60, 11 * 60 + 30, 12 * 60, 13 * 60].map((m) =>
            h('button', { class: 'vad-btn small', type: 'button', onclick: () => setAt(m) }, fmt12(m)),
          ),
        ),
        h(
          'p',
          { class: 'vad-note', style: 'margin:6px 0 0' },
          'Bands after this time drop; a band that straddles it is cut short and rings there.',
        ),
      );
    }

    add(card, 
      h(
        'div',
        { style: 'margin-top:10px' },
        h('label', { class: 'vad-lab', for: 'vad-note' }, 'Note on the tablets (optional)'),
        h('input', {
          class: 'vad-in',
          id: 'vad-note',
          type: 'text',
          maxlength: '120',
          value: ov?.note ?? '',
          placeholder: 'Snow day · Chapel runs long',
          'data-fkey': 'note',
          onchange: (e: Event) => {
            const text = (e.target as HTMLInputElement).value.trim();
            const target = ensureOverride(date);
            if (text) target.note = text;
            else delete target.note;
            tidyOverride(date);
            changed();
          },
        }),
      ),
    );

    if (draft.overrides[date]) {
      add(card, 
        h(
          'div',
          { class: 'vad-row', style: 'margin-top:10px' },
          h(
            'button',
            {
              class: 'vad-btn small danger',
              type: 'button',
              onclick: () => {
                void ask({
                  title: `Clear everything on ${shortDate(date)}?`,
                  body: 'This date goes back to the normal schedule — status, note and any bells you changed just for this date.',
                  confirm: 'Clear the date',
                }).then((yes) => {
                  if (!yes) return;
                  delete draft.overrides[date];
                  changed();
                });
              },
            },
            'Clear this date',
          ),
          h('span', { class: 'vad-note' }, 'Back to the normal schedule.'),
        ),
      );
    }
    return card;
  }

  // --------------------------------------------------------- the bells card

  function renderBellsCard(): HTMLElement {
    const card = h('div', { class: 'vad-card' });
    const scopeWord = scope === 'date' ? `${shortDate(date)} only` : 'every school day';
    add(card, 
      h('h2', {}, `Bells — ${scopeWord}`),
      h(
        'p',
        { class: 'vad-sub' },
        scope === 'date'
          ? `Times you set here ring on ${longDate(date)} and nowhere else.`
          : 'Times you set here become the permanent schedule.',
      ),
    );

    const dot = (id: GroupId): string =>
      same(groupBands(draft, id), groupBands(baseline, id)) &&
      same(draft.overrides[date]?.grids?.[id] ?? null, baseline.overrides[date]?.grids?.[id] ?? null)
        ? ''
        : ' •';
    add(card,
      h(
        'div',
        { class: 'vad-row' },
        segment(
          groupIds(draft).map((id) => ({ value: id, label: `${groupName(draft, id)}${dot(id)}` })),
          group,
          (v) => {
            group = v;
            previewGroup = group;
            openBand = null;
            scheduleRender();
          },
        ),
        h('span', { class: 'vad-spacer' }),
        scope === 'date' && hasDateBands(group)
          ? h(
              'button',
              {
                class: 'vad-btn small danger',
                type: 'button',
                onclick: () => {
                  const ov = draft.overrides[date];
                  if (ov?.grids) delete ov.grids[group];
                  tidyOverride(date);
                  changed();
                },
              },
              'Use the normal bells',
            )
          : null,
      ),
    );

    if (scope === 'date') {
      add(card,
        h(
          'p',
          { class: 'vad-note', style: 'margin:8px 0 0' },
          hasDateBands(group)
            ? `${groupName(draft, group)} has its own bells on ${shortDate(date)}.`
            : `${groupName(draft, group)} is showing the normal bells. The first change you make copies them onto ${shortDate(date)} only.`,
        ),
      );
    }

    const bands = shownBands(group);
    const bad = problems(bands);
    const published = bandsPublishedFor(group);
    const list = h('div', { class: 'vad-bands', style: 'margin-top:10px' });

    bands.forEach((band, index) => {
      add(list, renderBand(band, index, bands.length, bad.get(index) ?? null, published));
    });
    add(card, list);

    add(card, 
      h(
        'div',
        { class: 'vad-row', style: 'margin-top:10px' },
        h(
          'button',
          {
            class: 'vad-btn',
            type: 'button',
            onclick: () => {
              const target = editableBands(group);
              const last = target[target.length - 1];
              const start = last ? Math.min(1380, (last.end ?? last.start) + draft.passingMinutes) : 8 * 60;
              const band: Band = {
                id: `${group}-new-${start}-${Date.now().toString(36)}`,
                label: 'New band',
                kind: 'class',
                start,
                end: Math.min(1439, start + 50),
                ringStart: true,
                // "Upper School has passing" was never about the name: it is
                // about whether this group rings its bands OUT as well as in.
                // Ask the group's own bells, not a hardcoded id.
                ringEnd: ringsEnds(target.length ? target : groupBands(draft, group)),
                warn: true,
              };
              target.push(band);
              target.sort((a, b) => a.start - b.start);
              openBand = band.id;
              changed();
            },
          },
          '+ Add a band',
        ),
        h('span', { class: 'vad-note' }, `${bands.length} bands · ${bands.filter((b) => b.ringStart).length + bands.filter((b) => b.ringEnd).length} bells`),
      ),
    );

    return card;
  }

  /**
   * Does this bell group ring its bands out? Primary rings a band in and lets
   * it run; Upper rings both ends because passing starts there. A new band
   * should behave like the ones beside it, whatever the group is called.
   */
  function ringsEnds(bands: Band[]): boolean {
    const spans = bands.filter((b) => b.end !== null);
    if (!spans.length) return false;
    return spans.filter((b) => b.ringEnd).length * 2 >= spans.length;
  }

  function renderBand(
    band: Band,
    index: number,
    total: number,
    problem: string | null,
    published: Band[],
  ): HTMLElement {
    const before = published.find((b) => b.id === band.id);
    const moved = before && (before.start !== band.start || before.end !== band.end);
    const renamed = before && before.label !== band.label;
    const isNew = !before;
    const row = h('div', {
      class: `vad-band ${moved || renamed || isNew ? 'changed' : ''} ${problem ? 'bad' : ''}`,
    });

    const write = (mutate: (target: Band[], at: number) => void): void => {
      const target = editableBands(group);
      const at = target.findIndex((b) => b.id === band.id);
      if (at < 0) return;
      mutate(target, at);
      target.sort((a, b) => a.start - b.start);
      changed();
    };

    const canSwapUp = index > 0 && band.end !== null;
    const neighbourUp = shownBands(group)[index - 1];
    const neighbourDown = shownBands(group)[index + 1];

    add(row, 
      h(
        'div',
        { class: 'vad-band-top' },
        h('input', {
          class: 'vad-in vad-band-name',
          type: 'text',
          value: band.label,
          maxlength: '60',
          'aria-label': 'Band name',
          'data-fkey': `label:${band.id}`,
          onchange: (e: Event) => {
            const text = (e.target as HTMLInputElement).value.trim();
            if (!text) return;
            write((target, at) => { target[at]!.label = text; });
          },
        }),
        timeInput(band.start, `start:${band.id}`, (m) => {
          if (m === null) return;
          write((target, at) => { target[at]!.start = m; });
        }, false),
        h('span', { class: 'vad-dash' }, '–'),
        timeInput(band.end, `end:${band.id}`, (m) => {
          write((target, at) => { target[at]!.end = m; if (m === null) { target[at]!.warn = false; target[at]!.ringEnd = false; } });
        }, index === total - 1),
        h(
          'div',
          { class: 'vad-acts' },
          h(
            'button',
            {
              class: 'vad-icon',
              type: 'button',
              title: neighbourUp ? `Swap times with “${neighbourUp.label}”` : 'Nothing above',
              'aria-label': 'Swap with the band above',
              disabled: !canSwapUp || !neighbourUp || neighbourUp.end === null,
              onclick: () => write((target, at) => swapSpans(target, at, at - 1)),
            },
            '↑',
          ),
          h(
            'button',
            {
              class: 'vad-icon',
              type: 'button',
              title: neighbourDown ? `Swap times with “${neighbourDown.label}”` : 'Nothing below',
              'aria-label': 'Swap with the band below',
              disabled: !neighbourDown || neighbourDown.end === null || band.end === null,
              onclick: () => write((target, at) => swapSpans(target, at, at + 1)),
            },
            '↓',
          ),
          h(
            'button',
            {
              class: 'vad-icon',
              type: 'button',
              title: 'More: kind, bells, one-minute song',
              'aria-label': 'More settings',
              'aria-expanded': String(openBand === band.id),
              onclick: () => {
                openBand = openBand === band.id ? null : band.id;
                scheduleRender();
              },
            },
            openBand === band.id ? '⌃' : '⌄',
          ),
        ),
      ),
    );

    const flags: string[] = [];
    if (band.ringStart) flags.push('bell at start');
    if (band.ringEnd) flags.push('bell at end');
    if (band.ringBeforeEnd) flags.push(`bell ${band.ringBeforeEnd} min before it ends`);
    if (band.warn) flags.push('♪ one minute before it starts');
    add(row, 
      h(
        'div',
        { class: 'vad-meta' },
        h('span', {}, band.kind),
        h('span', {}, flags.length ? flags.join(' · ') : 'silent'),
        moved && before ? h('span', { class: 'vad-was' }, `was ${fmtSpan(before)}`) : null,
        isNew ? h('span', { class: 'vad-was' }, 'new') : null,
      ),
    );

    if (problem) add(row, h('div', { class: 'vad-err' }, problem));

    if (openBand === band.id) {
      add(row, 
        h(
          'div',
          { class: 'vad-more' },
          h(
            'div',
            {},
            h('span', { class: 'vad-lab' }, 'Kind'),
            segment(
              KINDS.map((k) => ({ value: k, label: k })),
              band.kind,
              (v) => write((target, at) => { target[at]!.kind = v as BandKind; }),
            ),
          ),
          h(
            'div',
            {},
            h('span', { class: 'vad-lab' }, 'Bells'),
            h(
              'div',
              { class: 'vad-flags' },
              toggle('Bell at start', band.ringStart, () =>
                write((target, at) => { target[at]!.ringStart = !target[at]!.ringStart; }),
              ),
              toggle('Bell at end', band.ringEnd, () =>
                write((target, at) => { target[at]!.ringEnd = !target[at]!.ringEnd; }),
                band.end === null,
              ),
              // NOT disabled for a band with no end: the song counts back from
              // the START, so Dismissal can have one — and should.
              toggle('♪ One-minute song', band.warn, () =>
                write((target, at) => { target[at]!.warn = !target[at]!.warn; }),
              ),
            ),
          ),
          // The specials rooms' packing-up bell. Hidden on a band with no end,
          // because there is nothing to count back from — the same reason
          // "Bell at end" is disabled there.
          band.end === null
            ? null
            : h(
                'div',
                {},
                h('span', { class: 'vad-lab' }, 'Before it ends'),
                segment(
                  BEFORE_END_CHOICES.map((m) => ({
                    value: String(m ?? 0),
                    label: m === null ? 'No bell' : `${m} min`,
                  })),
                  String(band.ringBeforeEnd ?? 0),
                  (v) =>
                    write((target, at) => {
                      const minutes = Number(v);
                      target[at]!.ringBeforeEnd = minutes > 0 ? minutes : null;
                    }),
                ),
              ),
          h(
            'div',
            { class: 'vad-row' },
            h(
              'button',
              {
                class: 'vad-btn small danger',
                type: 'button',
                onclick: () => {
                  void ask({
                    title: `Remove “${band.label}”?`,
                    body:
                      scope === 'date'
                        ? `It will not ring on ${longDate(date)}. Other days are untouched.`
                        : 'It will stop ringing on every school day.',
                    confirm: 'Remove the band',
                  }).then((yes) => {
                    if (!yes) return;
                    write((target, at) => { target.splice(at, 1); });
                  });
                },
              },
              'Remove this band',
            ),
          ),
        ),
      );
    }
    return row;
  }

  function toggle(label: string, on: boolean, onClick: () => void, disabled = false): HTMLElement {
    return h(
      'button',
      {
        class: 'vad-chip hair',
        type: 'button',
        'aria-pressed': String(on),
        disabled,
        onclick: onClick,
      },
      `${on ? '✓ ' : ''}${label}`,
    );
  }

  /**
   * Swap two bands' time spans. This is how "Upper eats second" is done: Lunch
   * and Period 4 trade slots in one tap, and nothing else on the day moves.
   */
  function swapSpans(bands: Band[], a: number, b: number): void {
    const one = bands[a];
    const two = bands[b];
    if (!one || !two || one.end === null || two.end === null) return;
    const start = one.start;
    const end = one.end;
    one.start = two.start;
    one.end = two.end;
    two.start = start;
    two.end = end;
  }

  // ---------------------------------------------------------- bell groups

  /** Everyone currently pointed at a group, in draft order. */
  function teachersIn(id: GroupId): Teacher[] {
    return draft.teachers.filter((t) => t.wing === id);
  }

  /** Dates that carry their own bells for a group — lost if it is deleted. */
  function datesWithBands(id: GroupId): string[] {
    return Object.entries(draft.overrides)
      .filter(([, ov]) => Boolean(ov.grids?.[id]))
      .map(([iso]) => iso)
      .sort();
  }

  function renderGroups(): HTMLElement {
    const card = h('div', { class: 'vad-card' });
    const ids = groupIds(draft);
    add(card,
      h('h2', {}, 'Bell groups'),
      h(
        'p',
        { class: 'vad-sub' },
        'A bell group is a set of rooms that ring together on one schedule. Every teacher belongs to exactly one.',
      ),
      h(
        'div',
        { class: 'vad-row' },
        h('button', { class: 'vad-btn primary', type: 'button', onclick: openNewGroup }, '+ New bell group'),
        h('span', { class: 'vad-note' }, `${ids.length} group${ids.length === 1 ? '' : 's'} · ${draft.teachers.length} tablets`),
      ),
      h('hr', { class: 'vad-rule' }),
    );

    for (const id of ids) {
      const grid = gridOf(draft, id);
      if (!grid) continue;
      const mine = teachersIn(id);
      const dates = datesWithBands(id);
      const wasPublished = gridOf(baseline, id);
      const renamed = wasPublished && wasPublished.title !== grid.title;
      const row = h('div', {
        class: 'vad-item',
        style: !wasPublished || renamed ? 'border-left:2px solid var(--gold)' : '',
      });

      add(row,
        h(
          'div',
          { class: 'vad-row' },
          h('b', { class: 'vad-groupname' }, grid.title),
          h('span', { class: 'vad-id' }, `id ${id}`),
          !wasPublished ? h('span', { class: 'vad-note' }, 'not published yet') : null,
        ),
        h(
          'p',
          { class: 'vad-note', style: 'margin:0' },
          [
            grid.subtitle || 'No subtitle',
            `${grid.bands.length} band${grid.bands.length === 1 ? '' : 's'}`,
            `${mine.length} teacher${mine.length === 1 ? '' : 's'}`,
            dates.length ? `${dates.length} date${dates.length === 1 ? '' : 's'} with their own bells` : null,
          ]
            .filter(Boolean)
            .join(' · '),
        ),
        h(
          'div',
          { class: 'vad-row' },
          h(
            'button',
            {
              class: 'vad-btn small',
              type: 'button',
              onclick: () => {
                group = id;
                previewGroup = id;
                openBand = null;
                tab = 'schedule';
                scheduleRender();
              },
            },
            'Edit its bells',
          ),
          h('button', { class: 'vad-btn small', type: 'button', onclick: () => openRenameGroup(id) }, 'Rename'),
          h(
            'button',
            { class: 'vad-btn small danger', type: 'button', onclick: () => openDeleteGroup(id) },
            'Delete',
          ),
        ),
      );
      add(card, row);
    }
    return card;
  }

  /**
   * A new group must be usable the moment it exists: an empty grid is a group
   * that rings nothing, and the server refuses to publish one. So the sheet
   * always starts it from somebody's day, or from one band you can edit.
   */
  function openNewGroup(): void {
    openSheet((close) => {
      let name = '';
      let from = groupIds(draft)[0] ?? 'blank';
      const idNote = h('p', { class: 'vad-note' }, 'The id is made from the name.');
      const error = h('p', { class: 'vad-flash bad' });

      const previewId = (): string =>
        freeId(slugify(name), groupIds(draft), 'bell-group');

      const refreshNote = (): void => {
        idNote.textContent = name.trim()
          ? `Its id will be “${previewId()}”. The id never changes, even if you rename the group later — it is what every teacher in it points at.`
          : 'The id is made from the name.';
      };

      const create = (): void => {
        const title = name.trim();
        if (!title) {
          error.textContent = 'Give the bell group a name — “Preschool”, “Athletics”.';
          return;
        }
        const id = previewId();
        const source = from === 'blank' ? null : gridOf(draft, from);
        const bands: Band[] = source
          ? source.bands.map((b) => ({ ...b, id: `${id}-${slugify(b.label) || 'band'}-${b.start}` }))
          : [
              {
                id: `${id}-first-bell-480`,
                label: 'First bell',
                kind: 'class',
                start: 8 * 60,
                end: 8 * 60 + 50,
                ringStart: true,
                ringEnd: false,
                warn: true,
              },
            ];
        // The subtitle is the line under the name on the wall ("Grades K – 6"),
        // so it is never copied: it would be wrong for the new group by
        // definition. Rename sets it.
        draft.grids[id] = { wing: id, title, subtitle: '', bands };
        group = id;
        previewGroup = id;
        tab = 'schedule';
        scope = 'default';
        openBand = null;
        close();
        flash = {
          kind: 'ok',
          text: `${title} created with ${bands.length} band${bands.length === 1 ? '' : 's'}. Set its bells, then publish.`,
        };
        render();
      };

      return h(
        'div',
        {},
        h('h2', {}, 'New bell group'),
        h(
          'p',
          { class: 'vad-sub' },
          'Rooms in one bell group ring together. Give it a name and a day to start from.',
        ),
        h(
          'div',
          {},
          h('label', { class: 'vad-lab', for: 'vad-newgroup' }, 'Name'),
          h('input', {
            class: 'vad-in',
            id: 'vad-newgroup',
            type: 'text',
            maxlength: '60',
            placeholder: 'Preschool',
            autocomplete: 'off',
            oninput: (e: Event) => {
              name = (e.target as HTMLInputElement).value;
              error.textContent = '';
              refreshNote();
            },
            onkeydown: (e: KeyboardEvent) => {
              if (e.key === 'Enter') create();
            },
          }),
          idNote,
        ),
        h(
          'div',
          { style: 'margin-top:10px' },
          h('label', { class: 'vad-lab', for: 'vad-newgroup-from' }, 'Start from'),
          h(
            'select',
            {
              class: 'vad-in',
              id: 'vad-newgroup-from',
              onchange: (e: Event) => {
                from = (e.target as HTMLSelectElement).value;
              },
            },
            ...groupIds(draft).map((id) =>
              h(
                'option',
                { value: id, selected: id === from },
                `A copy of ${groupName(draft, id)} — ${groupBands(draft, id).length} bands`,
              ),
            ),
            h('option', { value: 'blank' }, 'A blank day — one band to fill in'),
          ),
          h(
            'p',
            { class: 'vad-note' },
            'A copy is the fastest start: same bells, then move the ones that differ.',
          ),
        ),
        error,
        h(
          'div',
          { class: 'vad-sheet-acts' },
          h('button', { class: 'vad-btn', type: 'button', onclick: close }, 'Cancel'),
          h('button', { class: 'vad-btn primary', type: 'button', onclick: create }, 'Create the bell group'),
        ),
      );
    });
  }

  /** Renaming touches `Grid.title` and nothing else. The id is load-bearing. */
  function openRenameGroup(id: GroupId): void {
    const grid = gridOf(draft, id);
    if (!grid) return;
    openSheet((close) => {
      let title = grid.title;
      let subtitle = grid.subtitle;
      const error = h('p', { class: 'vad-flash bad' });

      const save = (): void => {
        const next = title.trim();
        if (!next) {
          error.textContent = 'A bell group needs a name.';
          return;
        }
        const target = gridOf(draft, id);
        if (!target) return;
        target.title = next;
        target.subtitle = subtitle.trim();
        close();
        changed();
      };

      return h(
        'div',
        {},
        h('h2', {}, `Rename “${grid.title}”`),
        h(
          'p',
          { class: 'vad-sub' },
          `Its id stays “${id}”, so every teacher in this bell group and every date with its own bells stays attached.`,
        ),
        h(
          'div',
          {},
          h('label', { class: 'vad-lab', for: 'vad-rename' }, 'Name'),
          h('input', {
            class: 'vad-in',
            id: 'vad-rename',
            type: 'text',
            maxlength: '60',
            value: title,
            oninput: (e: Event) => {
              title = (e.target as HTMLInputElement).value;
              error.textContent = '';
            },
            onkeydown: (e: KeyboardEvent) => {
              if (e.key === 'Enter') save();
            },
          }),
        ),
        h(
          'div',
          { style: 'margin-top:10px' },
          h('label', { class: 'vad-lab', for: 'vad-rename-sub' }, 'Subtitle on the tablet (optional)'),
          h('input', {
            class: 'vad-in',
            id: 'vad-rename-sub',
            type: 'text',
            maxlength: '80',
            value: subtitle,
            placeholder: 'Grades K – 6',
            oninput: (e: Event) => {
              subtitle = (e.target as HTMLInputElement).value;
            },
          }),
        ),
        error,
        h(
          'div',
          { class: 'vad-sheet-acts' },
          h('button', { class: 'vad-btn', type: 'button', onclick: close }, 'Cancel'),
          h('button', { class: 'vad-btn primary', type: 'button', onclick: save }, 'Save the name'),
        ),
      );
    });
  }

  /**
   * Deleting a group is the one edit that can silence a tablet without ever
   * touching a bell time, so it will not proceed while anybody is still in it:
   * either they are moved somewhere real, or nothing happens. The last group
   * cannot go at all.
   */
  function openDeleteGroup(id: GroupId): void {
    const grid = gridOf(draft, id);
    if (!grid) return;
    const others = groupIds(draft).filter((other) => other !== id);
    const mine = teachersIn(id);
    const dates = datesWithBands(id);

    openSheet((close) => {
      let moveTo = others[0] ?? '';
      const error = h('p', { class: 'vad-flash bad' });

      const remove = (): void => {
        if (!others.length) return;
        if (mine.length && !gridOf(draft, moveTo)) {
          error.textContent = 'Choose a bell group to move these teachers to.';
          return;
        }
        for (const teacher of draft.teachers) {
          if (teacher.wing === id) teacher.wing = moveTo;
        }
        for (const iso of Object.keys(draft.overrides)) {
          const ov = draft.overrides[iso];
          if (ov?.grids) {
            delete ov.grids[id];
            tidyOverride(iso);
          }
        }
        delete draft.grids[id];
        clampGroup();
        close();
        flash = {
          kind: 'ok',
          text: mine.length
            ? `${grid.title} deleted. ${mine.length} teacher${mine.length === 1 ? '' : 's'} moved to ${groupName(draft, moveTo)}. Publish to make it real.`
            : `${grid.title} deleted. Publish to make it real.`,
        };
        render();
      };

      const lost = [
        `${grid.bands.length} band${grid.bands.length === 1 ? '' : 's'}`,
        dates.length ? `bells set for ${dates.length} date${dates.length === 1 ? '' : 's'}` : null,
      ]
        .filter(Boolean)
        .join(' and ');

      if (!others.length) {
        return h(
          'div',
          {},
          h('h2', {}, `“${grid.title}” is the last bell group`),
          h(
            'p',
            { class: 'vad-sub' },
            'It cannot be deleted. With no bell groups every tablet in the building would ring nothing. Make another bell group first, move these rooms into it, then delete this one.',
          ),
          h(
            'div',
            { class: 'vad-sheet-acts' },
            h('button', { class: 'vad-btn', type: 'button', onclick: close }, 'Close'),
          ),
        );
      }

      return h(
        'div',
        {},
        h('h2', {}, `Delete “${grid.title}”?`),
        h(
          'p',
          { class: 'vad-sub' },
          `This throws away ${lost}. It cannot be undone from here — only by not publishing.`,
        ),
        mine.length
          ? h(
              'div',
              {},
              h(
                'div',
                { class: 'vad-confirm' },
                h(
                  'span',
                  {},
                  `${mine.length} teacher${mine.length === 1 ? ' is' : 's are'} still in this bell group: ${mine
                    .map((t) => t.name || t.roomLabel || t.id)
                    .join(', ')}. A tablet in a bell group that does not exist rings nothing all day, so they have to go somewhere.`,
                ),
              ),
              h('label', { class: 'vad-lab', for: 'vad-move-to' }, 'Move them to'),
              h(
                'select',
                {
                  class: 'vad-in',
                  id: 'vad-move-to',
                  onchange: (e: Event) => {
                    moveTo = (e.target as HTMLSelectElement).value;
                    error.textContent = '';
                  },
                },
                ...others.map((other) =>
                  h('option', { value: other, selected: other === moveTo }, groupName(draft, other)),
                ),
              ),
            )
          : null,
        error,
        h(
          'div',
          { class: 'vad-sheet-acts' },
          h('button', { class: 'vad-btn', type: 'button', onclick: close }, 'Keep it'),
          h(
            'button',
            { class: 'vad-btn danger', type: 'button', onclick: remove },
            mine.length ? 'Move them and delete' : 'Delete the bell group',
          ),
        ),
      );
    });
  }

  // ------------------------------------------------------------- teachers

  function renderTeachers(): HTMLElement {
    const card = h('div', { class: 'vad-card' });
    add(card,
      h('h2', {}, 'Tablets and teachers'),
      h(
        'p',
        { class: 'vad-sub' },
        'One slot per tablet. Names, bell groups and rooms publish with the schedule; a PIN is saved the moment you set it, once that teacher is published.',
      ),
      h(
        'div',
        { class: 'vad-row' },
        h('button', { class: 'vad-btn primary', type: 'button', onclick: openNewTeacher }, '+ Add a teacher'),
        h(
          'span',
          { class: 'vad-note' },
          `${draft.teachers.length} slot${draft.teachers.length === 1 ? '' : 's'} · ${draft.teachers.filter((t) => t.active).length} in use`,
        ),
      ),
      h('hr', { class: 'vad-rule' }),
    );

    draft.teachers.forEach((teacher, index) => {
      add(card, renderTeacher(teacher, index));
    });
    return card;
  }

  /**
   * The order here is the whole point. A credential must never point at
   * somebody the published document has never heard of — the tablet would sign
   * in as a person with no bell group and ring nothing — so a new teacher is
   * written into the draft, and any PIN typed for them waits until the publish
   * that creates them lands.
   */
  function openNewTeacher(): void {
    openSheet((close) => {
      let name = '';
      let room = '';
      let pin = '';
      let inGroup = groupIds(draft).includes(group) ? group : (groupIds(draft)[0] ?? '');
      const error = h('p', { class: 'vad-flash bad' });

      const create = (): void => {
        const who = name.trim();
        if (!who) {
          error.textContent = 'Give the teacher a name — it is what the tablet shows all day.';
          return;
        }
        if (!gridOf(draft, inGroup)) {
          error.textContent = 'Choose a bell group.';
          return;
        }
        if (pin && !/^\d{4,6}$/.test(pin)) {
          error.textContent = 'A PIN is 4 to 6 digits, or leave it empty and set it later.';
          return;
        }
        const id = freeId(
          slugify(who),
          draft.teachers.map((t) => t.id),
          'teacher',
        );
        draft.teachers.push({
          id,
          name: who,
          wing: inGroup,
          roomLabel: room.trim(),
          role: 'teacher',
          active: true,
        });
        if (pin) pinAfterPublish.set(id, pin);
        close();
        flash = {
          kind: 'ok',
          text: pin
            ? `${who} added to ${groupName(draft, inGroup)}. Publish, and their PIN is saved the moment it lands.`
            : `${who} added to ${groupName(draft, inGroup)}. Publish to put them on the tablets.`,
        };
        render();
      };

      return h(
        'div',
        {},
        h('h2', {}, 'Add a teacher'),
        h(
          'p',
          { class: 'vad-sub' },
          'One teacher is one tablet: a name, the bell group it rings on, and a PIN to sign in with.',
        ),
        h(
          'div',
          {},
          h('label', { class: 'vad-lab', for: 'vad-newteacher' }, 'Name'),
          h('input', {
            class: 'vad-in',
            id: 'vad-newteacher',
            type: 'text',
            maxlength: '60',
            placeholder: 'Mrs. Ruiz',
            autocomplete: 'off',
            oninput: (e: Event) => {
              name = (e.target as HTMLInputElement).value;
              error.textContent = '';
            },
          }),
        ),
        h(
          'div',
          { style: 'margin-top:10px' },
          h('label', { class: 'vad-lab', for: 'vad-newteacher-group' }, 'Bell group'),
          h(
            'select',
            {
              class: 'vad-in',
              id: 'vad-newteacher-group',
              onchange: (e: Event) => {
                inGroup = (e.target as HTMLSelectElement).value;
                error.textContent = '';
              },
            },
            ...groupIds(draft).map((id) =>
              h('option', { value: id, selected: id === inGroup }, groupName(draft, id)),
            ),
          ),
        ),
        h(
          'div',
          { style: 'margin-top:10px' },
          h('label', { class: 'vad-lab', for: 'vad-newteacher-room' }, 'Room label (optional)'),
          h('input', {
            class: 'vad-in',
            id: 'vad-newteacher-room',
            type: 'text',
            maxlength: '40',
            placeholder: 'Room 12',
            oninput: (e: Event) => {
              room = (e.target as HTMLInputElement).value;
            },
          }),
        ),
        h(
          'div',
          { style: 'margin-top:10px' },
          h('label', { class: 'vad-lab', for: 'vad-newteacher-pin' }, 'PIN (optional, 4 to 6 digits)'),
          h('input', {
            class: 'vad-in',
            id: 'vad-newteacher-pin',
            type: 'password',
            inputmode: 'numeric',
            autocomplete: 'off',
            maxlength: '6',
            placeholder: '4–6 digits',
            oninput: (e: Event) => {
              const el = e.target as HTMLInputElement;
              el.value = el.value.replace(/\D/g, '');
              pin = el.value;
              error.textContent = '';
            },
          }),
          h(
            'p',
            { class: 'vad-note' },
            'Nobody can share a PIN — it is how a tablet knows which bell group it rings. This one is saved right after the publish that adds them.',
          ),
        ),
        error,
        h(
          'div',
          { class: 'vad-sheet-acts' },
          h('button', { class: 'vad-btn', type: 'button', onclick: close }, 'Cancel'),
          h('button', { class: 'vad-btn primary', type: 'button', onclick: create }, 'Add the teacher'),
        ),
      );
    });
  }

  /**
   * Removing a teacher clears their PIN FIRST, on the server, and only then
   * takes the row out of the draft. A live credential for somebody who is no
   * longer in the document would still sign a tablet in off the stored
   * fallback — a deleted teacher with a working PIN is a real hole.
   */
  function removeTeacher(teacher: Teacher): void {
    const who = teacher.name || teacher.roomLabel || teacher.id;
    void ask({
      title: `Remove ${who}?`,
      body: `Their PIN is cleared straight away, and the slot leaves ${groupName(draft, teacher.wing)} when you publish. A tablet signed in as ${who} stops being able to sign in again.`,
      confirm: 'Clear the PIN and remove',
    }).then(async (yes) => {
      if (!yes) return;
      pinDraft.delete(teacher.id);
      pinAfterPublish.delete(teacher.id);

      // Only published teachers can hold a credential, and the worker answers
      // 404 for anybody else — so do not ask it about a row it has never seen.
      const published = store.doc.teachers.some((t) => t.id === teacher.id);
      let cleared = true;
      if (published && hasPin.get(teacher.id) !== false) {
        cleared = await clearPinFor(teacher.id);
      }
      if (!cleared) {
        pinNote.set(teacher.id, {
          kind: 'bad',
          text: 'Their PIN could not be cleared, so the slot was kept. Try again.',
        });
        render();
        return;
      }
      const at = draft.teachers.findIndex((t) => t.id === teacher.id);
      if (at >= 0) draft.teachers.splice(at, 1);
      hasPin.delete(teacher.id);
      pinNote.delete(teacher.id);
      flash = {
        kind: 'ok',
        text: `${who}’s PIN is cleared. Publish to take the slot off the tablets.`,
      };
      render();
    });
  }

  /** Straight to the worker. True when the credential is definitely gone. */
  async function clearPinFor(teacherId: string): Promise<boolean> {
    try {
      const res = await fetch('/api/admin/teachers', {
        method: 'POST',
        headers: withAuth({ 'content-type': 'application/json' }),
        body: JSON.stringify({ action: 'clearPin', teacherId }),
      });
      const body = (await res.json().catch(() => null)) as { ok?: boolean } | null;
      return res.ok && body?.ok !== false;
    } catch {
      return false;
    }
  }

  function renderTeacher(teacher: Teacher, index: number): HTMLElement {
    const before = baseline.teachers.find((t) => t.id === teacher.id);
    const isChanged = !same(before ?? null, teacher);
    const row = h('div', { class: 'vad-teacher', style: isChanged ? 'border-left:2px solid var(--gold)' : '' });

    const write = (mutate: (t: Teacher) => void): void => {
      const target = draft.teachers[index];
      if (!target) return;
      mutate(target);
      changed();
    };

    add(row, 
      h(
        'div',
        { class: 'vad-grid2' },
        h(
          'div',
          {},
          h('span', { class: 'vad-lab' }, `Slot ${index + 1} · name`),
          h('input', {
            class: 'vad-in',
            type: 'text',
            value: teacher.name,
            maxlength: '60',
            placeholder: 'Unnamed slot',
            'data-fkey': `tname:${teacher.id}`,
            onchange: (e: Event) => {
              const name = (e.target as HTMLInputElement).value.trim();
              write((t) => {
                t.name = name;
                if (name && !t.active) t.active = true;
              });
            },
          }),
        ),
        h(
          'div',
          {},
          h('span', { class: 'vad-lab' }, 'Room label'),
          h('input', {
            class: 'vad-in',
            type: 'text',
            value: teacher.roomLabel,
            maxlength: '40',
            placeholder: 'Room 3',
            'data-fkey': `troom:${teacher.id}`,
            onchange: (e: Event) => {
              const value = (e.target as HTMLInputElement).value.trim();
              write((t) => { t.roomLabel = value; });
            },
          }),
        ),
      ),
      h(
        'div',
        { class: 'vad-row' },
        h('label', { class: 'vad-lab', style: 'margin:0', for: `vad-tgroup-${teacher.id}` }, 'Bell group'),
        h(
          'select',
          {
            class: 'vad-in vad-pick',
            id: `vad-tgroup-${teacher.id}`,
            'data-fkey': `tgroup:${teacher.id}`,
            onchange: (e: Event) => {
              const value = (e.target as HTMLSelectElement).value;
              write((t) => { t.wing = value; });
            },
          },
          ...groupIds(draft).map((id) =>
            h('option', { value: id, selected: id === teacher.wing }, groupName(draft, id)),
          ),
          // A group that has gone missing must stay visible and selected, or a
          // silent re-point would look like a fix.
          gridOf(draft, teacher.wing)
            ? null
            : h('option', { value: teacher.wing, selected: true }, `${teacher.wing} (missing)`),
        ),
        h('span', { class: 'vad-spacer' }),
        toggle(teacher.active ? 'In use' : 'Not in use', teacher.active, () =>
          write((t) => { t.active = !t.active; }),
        ),
        h(
          'button',
          { class: 'vad-btn small danger', type: 'button', onclick: () => removeTeacher(teacher) },
          'Remove',
        ),
      ),
      gridOf(draft, teacher.wing)
        ? null
        : h(
            'div',
            { class: 'vad-err' },
            `This tablet is in a bell group that does not exist (“${teacher.wing}”). It would ring nothing. Move them to a real bell group.`,
          ),
    );

    const note = pinNote.get(teacher.id);
    const pinned = hasPin.get(teacher.id);
    const queued = pinAfterPublish.has(teacher.id);
    add(row,
      h(
        'div',
        { class: 'vad-pin' },
        h(
          'span',
          { class: 'vad-lab', style: 'margin:0' },
          queued
            ? 'PIN · waiting for publish'
            : pinned === undefined
              ? 'PIN'
              : pinned
                ? 'PIN · set'
                : 'PIN · none yet',
        ),
        h('input', {
          class: 'vad-in',
          type: 'password',
          inputmode: 'numeric',
          autocomplete: 'off',
          maxlength: '6',
          placeholder: '4–6 digits',
          value: pinDraft.get(teacher.id) ?? '',
          'aria-label': `PIN for ${teacher.name || `slot ${index + 1}`}`,
          'data-fkey': `tpin:${teacher.id}`,
          oninput: (e: Event) => {
            const el = e.target as HTMLInputElement;
            el.value = el.value.replace(/\D/g, '');
            pinDraft.set(teacher.id, el.value);
          },
        }),
        h(
          'button',
          {
            class: 'vad-btn small',
            type: 'button',
            onclick: () => void savePin(teacher, pinDraft.get(teacher.id) ?? ''),
          },
          'Set PIN',
        ),
        h(
          'button',
          {
            class: 'vad-btn small danger',
            type: 'button',
            onclick: () => {
              void ask({
                title: `Clear ${teacher.name || 'this slot'}’s PIN?`,
                body: 'That tablet will not be able to sign in until a new PIN is set.',
                confirm: 'Clear the PIN',
              }).then((yes) => {
                if (yes) void savePin(teacher, null);
              });
            },
          },
          'Clear',
        ),
        note ? h('span', { class: `vad-note ${note.kind}` }, note.text) : null,
      ),
    );
    return row;
  }

  /** Ask the worker which slots already carry a PIN. Read-only, best effort. */
  async function loadPinState(): Promise<void> {
    try {
      const res = await fetch('/api/admin/teachers', {
        method: 'POST',
        headers: withAuth({ 'content-type': 'application/json' }),
        body: JSON.stringify({ action: 'list' }),
      });
      const body = (await res.json().catch(() => null)) as
        | { ok?: boolean; teachers?: { id: string; hasPin?: boolean }[] }
        | null;
      if (!body?.ok || !body.teachers) return;
      for (const t of body.teachers) hasPin.set(t.id, Boolean(t.hasPin));
      render();
    } catch {
      // The tab still works without it; the label just stays neutral.
    }
  }

  /**
   * A PIN is saved the instant it is typed — it is a credential, not part of
   * the schedule document, and waiting for a publish to make a tablet usable
   * would be a trap on setup night.
   *
   * The one exception is a teacher who is not published yet. The worker builds
   * a credential around the identity it finds in the PUBLISHED document, so a
   * PIN attached to a row nobody has published would be a sign-in with no bell
   * group behind it. Those PINs are held here, said out loud on the row, and
   * sent the moment the publish that creates the teacher lands.
   *
   * The bell group is never sent with a PIN: the published document owns it,
   * and the worker reads it from there. Name and room are sent when this row
   * has been edited and not yet published, so the tablet does not sign in as
   * the old name.
   */
  async function savePin(teacher: Teacher, pin: string | null): Promise<{ ok: boolean; text: string }> {
    // 4-6, not 4-8: the classroom PIN pad physically stops at six digits, so a
    // longer PIN would be set here and then be untypeable on the wall.
    if (pin !== null && !/^\d{4,6}$/.test(pin)) {
      const text = 'A PIN is 4 to 6 digits.';
      pinNote.set(teacher.id, { kind: 'bad', text });
      scheduleRender();
      return { ok: false, text };
    }

    const live = store.doc.teachers.find((t) => t.id === teacher.id);
    const who = teacher.name || teacher.roomLabel || 'This slot';
    if (!live) {
      // Not on the tablets yet. Say so plainly rather than sending a
      // credential the worker would have to invent an identity for.
      if (pin === null) {
        pinAfterPublish.delete(teacher.id);
        const text = `Nothing to clear — ${who} is not published yet.`;
        pinNote.set(teacher.id, { kind: 'wait', text });
        scheduleRender();
        return { ok: true, text };
      }
      pinAfterPublish.set(teacher.id, pin);
      pinDraft.delete(teacher.id);
      const text = `Not live yet: ${who} is not on the tablets until you publish. This PIN is saved the moment that publish lands.`;
      pinNote.set(teacher.id, { kind: 'wait', text });
      scheduleRender();
      return { ok: true, text };
    }

    pinNote.set(teacher.id, { kind: 'ok', text: 'Saving…' });
    scheduleRender();

    const identityMoved = live.name !== teacher.name || live.roomLabel !== teacher.roomLabel;
    const groupMoved = live.wing !== teacher.wing;
    const payload: Record<string, unknown> = pin === null
      ? { action: 'clearPin', teacherId: teacher.id }
      : { teacherId: teacher.id, pin };
    if (identityMoved) {
      payload['name'] = teacher.name;
      payload['roomLabel'] = teacher.roomLabel;
      payload['active'] = teacher.active;
    }

    try {
      const res = await fetch('/api/admin/teachers', {
        method: 'POST',
        headers: withAuth({ 'content-type': 'application/json' }),
        body: JSON.stringify(payload),
      });
      const body = (await res.json().catch(() => null)) as
        | { ok?: boolean; error?: string; hasPin?: boolean; teacher?: Teacher }
        | null;
      if (res.status === 401 || res.status === 403) {
        pinNote.set(teacher.id, { kind: 'bad', text: SIGNED_OUT });
      } else if (!res.ok || body?.ok === false) {
        pinNote.set(teacher.id, { kind: 'bad', text: body?.error ?? `Server said ${res.status}.` });
      } else {
        pinDraft.delete(teacher.id);
        pinAfterPublish.delete(teacher.id);
        hasPin.set(teacher.id, body?.hasPin ?? pin !== null);
        pinNote.set(teacher.id, {
          kind: 'ok',
          text:
            pin === null
              ? 'PIN cleared.'
              : groupMoved
                ? `PIN saved. Publish to move ${who} to ${groupName(draft, teacher.wing)}.`
                : identityMoved
                  ? 'PIN and name saved.'
                  : 'PIN saved.',
        });
        if (identityMoved && body?.teacher) {
          // The worker just published this row, so it is no longer a pending edit.
          const at = baseline.teachers.findIndex((t) => t.id === teacher.id);
          if (at >= 0) baseline.teachers[at] = { ...clone(body.teacher), wing: teacher.wing };
          await Promise.resolve(store.refresh()).catch(() => undefined);
        }
      }
    } catch (err) {
      pinNote.set(teacher.id, {
        kind: 'bad',
        text: err instanceof Error ? `Could not reach the server: ${err.message}` : 'Could not reach the server.',
      });
    }
    render();
    const settled = pinNote.get(teacher.id);
    return { ok: settled?.kind === 'ok', text: settled?.text ?? '' };
  }

  /**
   * Send the PINs that were waiting on a publish. Called immediately after a
   * successful one, when the teachers they belong to are finally in the
   * document the worker will read.
   */
  async function flushQueuedPins(): Promise<string> {
    if (!pinAfterPublish.size) return '';
    const done: string[] = [];
    for (const [id, pin] of [...pinAfterPublish.entries()]) {
      const teacher = draft.teachers.find((t) => t.id === id);
      const live = store.doc.teachers.find((t) => t.id === id);
      // Still not published (the publish failed, or somebody removed the row):
      // leave it queued and keep saying so on the row.
      if (!teacher || !live) continue;
      pinAfterPublish.delete(id);
      const who = teacher.name || teacher.roomLabel || id;
      const result = await savePin(teacher, pin);
      done.push(result.ok ? `${who} can now sign in with their PIN.` : `${who}’s PIN was refused: ${result.text}`);
    }
    return done.join(' ');
  }

  // -------------------------------------------------------------- changes

  function describeChanges(): string[] {
    const lines: string[] = [];

    // Bell groups first: everything below is read against them.
    for (const id of groupIds(draft)) {
      const now = gridOf(draft, id)!;
      const was = gridOf(baseline, id);
      if (!was) {
        lines.push(
          `New bell group “${now.title}” with ${now.bands.length} band${now.bands.length === 1 ? '' : 's'}.`,
        );
        continue;
      }
      if (was.title !== now.title) lines.push(`Bell group “${was.title}” renamed “${now.title}”.`);
      if (was.subtitle !== now.subtitle) {
        lines.push(
          now.subtitle
            ? `“${now.title}” now reads “${now.subtitle}” on the tablet.`
            : `“${now.title}” has no subtitle on the tablet.`,
        );
      }
      lines.push(...diffBands(was.bands, now.bands, `${now.title} · every school day`));
    }
    for (const id of groupIds(baseline)) {
      if (!gridOf(draft, id)) lines.push(`Bell group “${groupName(baseline, id)}” deleted.`);
    }

    const dates = new Set([...Object.keys(baseline.overrides), ...Object.keys(draft.overrides)]);
    for (const iso of [...dates].sort()) {
      const was = baseline.overrides[iso];
      const now = draft.overrides[iso];
      const when = shortDate(iso);
      if (was && !now) {
        lines.push(`${when}: back to a normal day.`);
        continue;
      }
      if (!now) continue;
      // A date only gets an entry because bells were edited for it; do not
      // announce "a normal day" as though something happened to the day itself.
      const statusMoved = was
        ? was.status !== now.status ||
          was.delayMinutes !== now.delayMinutes ||
          was.dismissAt !== now.dismissAt
        : now.status !== 'normal';
      if (statusMoved) lines.push(`${when}: ${statusPhrase(now)}.`);
      if ((was?.note ?? '') !== (now.note ?? '')) {
        lines.push(now.note ? `${when}: note “${now.note}”.` : `${when}: note removed.`);
      }
      const touched = new Set([...Object.keys(was?.grids ?? {}), ...Object.keys(now.grids ?? {})]);
      for (const id of touched) {
        const wasBands = was?.grids?.[id] ?? null;
        const nowBands = now.grids?.[id] ?? null;
        if (same(wasBands, nowBands)) continue;
        const label = gridOf(draft, id) ? groupName(draft, id) : groupName(baseline, id);
        if (!nowBands) {
          lines.push(`${when} · ${label}: back to the normal bells.`);
          continue;
        }
        const from = wasBands ?? groupBands(baseline, id);
        const detail = diffBands(from, nowBands, `${when} · ${label} (this date only)`);
        lines.push(...(detail.length ? detail : [`${when} · ${label}: bells set for this date only.`]));
      }
    }

    for (const teacher of draft.teachers) {
      const was = baseline.teachers.find((t) => t.id === teacher.id);
      const where = groupName(draft, teacher.wing);
      if (!was) {
        lines.push(
          `New tablet for “${teacher.name || teacher.id}” in ${where}${teacher.roomLabel ? `, ${teacher.roomLabel}` : ''}.`,
        );
        continue;
      }
      const who = was.name || teacher.name || teacher.id;
      if (was.name !== teacher.name) {
        lines.push(teacher.name ? `Slot “${was.name || teacher.id}” is now ${teacher.name}.` : `${who} cleared from the slot.`);
      }
      if (was.wing !== teacher.wing) lines.push(`${who} moves to ${where}.`);
      if (was.roomLabel !== teacher.roomLabel) {
        lines.push(teacher.roomLabel ? `${who} is in ${teacher.roomLabel}.` : `${who} has no room label.`);
      }
      if (was.active !== teacher.active) {
        lines.push(`${who} tablet is now ${teacher.active ? 'in use' : 'not in use'}.`);
      }
    }
    for (const was of baseline.teachers) {
      if (!draft.teachers.some((t) => t.id === was.id)) {
        lines.push(`${was.name || was.roomLabel || was.id} removed from the tablets.`);
      }
    }
    return lines;
  }

  function statusPhrase(ov: DayOverride): string {
    switch (ov.status) {
      case 'closed':
        return 'closed — no bells';
      case 'delayed':
        return `delayed start, everything ${ov.delayMinutes ?? 0} minutes later`;
      case 'early':
        return `early dismissal at ${fmt12(ov.dismissAt ?? 0)}`;
      default:
        return 'a normal day';
    }
  }

  function diffBands(was: Band[], now: Band[], where: string): string[] {
    const lines: string[] = [];
    const wasById = new Map(was.map((b) => [b.id, b]));
    const nowIds = new Set(now.map((b) => b.id));
    for (const band of now) {
      const before = wasById.get(band.id);
      if (!before) {
        lines.push(`${where}: “${band.label}” added at ${fmtSpan(band)}.`);
        continue;
      }
      if (before.start !== band.start || before.end !== band.end) {
        lines.push(`${where}: “${band.label}” moves ${fmtSpan(before)} → ${fmtSpan(band)}.`);
      }
      if (before.label !== band.label) {
        lines.push(`${where}: “${before.label}” renamed “${band.label}”.`);
      }
      if (
        before.ringStart !== band.ringStart ||
        before.ringEnd !== band.ringEnd ||
        (before.ringBeforeEnd ?? null) !== (band.ringBeforeEnd ?? null) ||
        before.warn !== band.warn
      ) {
        lines.push(`${where}: “${band.label}” bells changed.`);
      }
      if (before.kind !== band.kind) {
        lines.push(`${where}: “${band.label}” is now a ${band.kind} band.`);
      }
    }
    for (const band of was) {
      if (!nowIds.has(band.id)) lines.push(`${where}: “${band.label}” removed.`);
    }
    return lines;
  }

  // -------------------------------------------------------------- publishing

  /**
   * School days are a property of the document, not of any one bell group, so
   * this asks whichever group is at hand — the one being edited if it is real,
   * otherwise the first.
   */
  function nextSchoolDate(doc: ScheduleDoc, fromISO: string): string | null {
    const id = gridOf(doc, group) ? group : groupIds(doc)[0];
    if (!id) return null;
    for (let i = 0; i < 30; i++) {
      const iso = addDays(fromISO, i);
      const { plan } = planOf(doc, iso, id);
      if (plan?.isSchoolDay && plan.status !== 'closed') return iso;
    }
    return null;
  }

  /** Would publishing this leave a bell group with nothing to ring? */
  function silenceRisks(): string[] {
    const risks: string[] = [];
    const today = todayISO(clock.now());
    const ids = groupIds(draft);
    if (!ids.length) {
      risks.push('There are no bell groups left. Every tablet in the building will ring nothing.');
      return risks;
    }
    const orphans = draft.teachers.filter((t) => t.active && !gridOf(draft, t.wing));
    for (const teacher of orphans) {
      risks.push(
        `${teacher.name || teacher.roomLabel || teacher.id} is in a bell group that does not exist, so that tablet rings NOTHING.`,
      );
    }
    const target = nextSchoolDate(draft, today);
    if (!target) {
      risks.push('There is no school day in the next 30 days. The tablets will ring nothing at all.');
      return risks;
    }
    for (const id of ids) {
      const { plan } = planOf(draft, target, id);
      if (plan && plan.isSchoolDay && !plan.events.length) {
        risks.push(`${groupName(draft, id)} rings NOTHING on ${longDate(target)}, the next school day.`);
      }
    }
    if (scope === 'date' && date !== target) {
      for (const id of ids) {
        const { plan } = planOf(draft, date, id);
        if (plan && plan.isSchoolDay && plan.status !== 'closed' && !plan.events.length) {
          risks.push(`${groupName(draft, id)} rings NOTHING on ${longDate(date)}.`);
        }
      }
    }
    return risks;
  }

  function openPublish(): void {
    const lines = describeChanges();
    const risks = silenceRisks();
    const blockers = anyProblems(draft);
    let acknowledged = risks.length === 0;

    openSheet((close) => {
      const message = h('p', { class: 'vad-flash' });
      const publishBtn = h(
        'button',
        { class: 'vad-btn primary', type: 'button', disabled: !acknowledged },
        blockers.length ? 'Publish anyway' : 'Publish now',
      ) as HTMLButtonElement;

      publishBtn.addEventListener('click', () => {
        publishBtn.disabled = true;
        publishBtn.textContent = 'Publishing…';
        message.className = 'vad-flash';
        message.textContent = '';
        void publish().then((result) => {
          if (result.ok) {
            close();
            flash = {
              kind: 'ok',
              text: `Published version ${result.version}. Every tablet has it inside 60 seconds.${
                result.pins ? ` ${result.pins}` : ''
              }`,
            };
            render();
          } else {
            message.className = 'vad-flash bad';
            message.textContent = result.error ?? 'The server refused this schedule.';
            publishBtn.disabled = false;
            publishBtn.textContent = 'Try again';
          }
        });
      });

      const body = h(
        'div',
        {},
        h('h2', {}, 'Publish to eleven tablets'),
        h(
          'p',
          { class: 'vad-sub' },
          store.doc.version !== baseline.version
            ? `Careful: someone else published version ${store.doc.version} while you were editing.`
            : `Currently published: version ${store.doc.version}.`,
        ),
        lines.length
          ? h('ul', { class: 'vad-list' }, ...lines.map((line) => h('li', {}, line)))
          : h('p', { class: 'vad-sub' }, 'Nothing has changed yet.'),
        blockers.length
          ? h(
              'div',
              { class: 'vad-warn' },
              ...blockers.map((b) => h('div', {}, b)),
              h('div', { class: 'soft' }, 'The server checks this too, and will refuse if it still looks wrong.'),
            )
          : null,
        ...risks.map((risk) =>
          h(
            'label',
            { class: 'vad-confirm' },
            h('input', {
              type: 'checkbox',
              onchange: (e: Event) => {
                acknowledged = (e.target as HTMLInputElement).checked;
                publishBtn.disabled = !acknowledged;
              },
            }),
            h('span', {}, `I understand: ${risk}`),
          ),
        ),
        message,
        h(
          'div',
          { class: 'vad-sheet-acts' },
          h('button', { class: 'vad-btn', type: 'button', onclick: close }, 'Not yet'),
          publishBtn,
        ),
      );
      return body;
    });
  }

  async function publish(): Promise<PublishResponse & { pins?: string }> {
    try {
      const res = await fetch('/api/admin/publish', {
        method: 'POST',
        headers: withAuth({ 'content-type': 'application/json' }),
        body: JSON.stringify({ doc: draft }),
      });
      const body = (await res.json().catch(() => null)) as PublishResponse | null;
      if (res.status === 401 || res.status === 403) {
        return { ok: false, error: SIGNED_OUT };
      }
      if (!res.ok || !body?.ok) {
        return { ok: false, error: body?.error ?? `The server refused it (${res.status}).` };
      }
      baseline = clone(draft);
      baseline.version = body.version ?? baseline.version;
      draft.version = baseline.version;
      await Promise.resolve(store.refresh()).catch(() => undefined);
      // The teachers this publish created now exist in the document the worker
      // reads, so any PIN typed for them can finally be attached.
      const pins = await flushQueuedPins();
      return pins ? { ...body, pins } : body;
    } catch (err) {
      return {
        ok: false,
        error: err instanceof Error ? `Could not reach the server: ${err.message}` : 'Could not reach the server.',
      };
    }
  }

  function withAuth(base: Record<string, string>): Record<string, string> {
    try {
      const extra = authHeader() as unknown;
      if (extra instanceof Headers) extra.forEach((value, key) => { base[key] = value; });
      else if (Array.isArray(extra)) for (const [key, value] of extra as [string, string][]) base[key] = value;
      else if (extra && typeof extra === 'object') Object.assign(base, extra as Record<string, string>);
    } catch {
      // A missing token still gets a 401 from the worker, which we show plainly.
    }
    return base;
  }

  // ------------------------------------------------------------------ sheets

  function openSheet(build: (close: () => void) => Node, onDismiss?: () => void): void {
    const overlay = h('div', { class: 'vad-overlay' });
    const close = (): void => {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    };
    /** Backdrop or Escape — the user walked away rather than answering. */
    const dismiss = (): void => {
      close();
      onDismiss?.();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') dismiss();
    };
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) dismiss();
    });
    document.addEventListener('keydown', onKey);
    add(overlay, h('div', { class: 'vad-sheet', role: 'dialog', 'aria-modal': 'true' }, build(close)));
    add(shell, overlay);
    overlay.querySelector<HTMLElement>('.vad-btn')?.focus();
  }

  function ask(opts2: { title: string; body: string; confirm: string }): Promise<boolean> {
    return new Promise((resolve) => {
      let answered = false;
      const settle = (value: boolean): void => {
        if (answered) return;
        answered = true;
        resolve(value);
      };
      openSheet((close) => {
        const done = (value: boolean): void => {
          close();
          settle(value);
        };
        return h(
          'div',
          {},
          h('h2', {}, opts2.title),
          h('p', { class: 'vad-sub' }, opts2.body),
          h(
            'div',
            { class: 'vad-sheet-acts' },
            h('button', { class: 'vad-btn', type: 'button', onclick: () => done(false) }, 'Keep it'),
            h('button', { class: 'vad-btn danger', type: 'button', onclick: () => done(true) }, opts2.confirm),
          ),
        );
      }, () => settle(false));
    });
  }

  function exit(): void {
    if (!dirty()) {
      onExit();
      return;
    }
    void ask({
      title: 'Leave without publishing?',
      body: `You have ${describeChanges().length || 'unpublished'} change${describeChanges().length === 1 ? '' : 's'} that no tablet has seen. Leaving throws them away.`,
      confirm: 'Discard and leave',
    }).then((yes) => {
      if (yes) {
        baseline = clone(draft);
        onExit();
      }
    });
  }

  // ------------------------------------------------------------------ render

  function render(): void {
    clampGroup();
    const focused = document.activeElement as HTMLElement | null;
    const fkey = focused?.getAttribute?.('data-fkey') ?? null;
    const caret = focused instanceof HTMLInputElement ? focused.selectionStart : null;

    topRow.replaceChildren(
      h('span', { class: 'vad-mark' }, 'Valor ', h('b', {}, 'Bell Editor')),
      h('span', { class: 'vad-who' }, `${session.name || session.teacherId} · ${session.role}`),
      h('button', { class: 'vad-btn small', type: 'button', onclick: exit }, 'Exit to kiosk'),
    );

    const TAB_LABEL = {
      schedule: 'Schedule',
      groups: 'Bell groups',
      teachers: 'Tablets & PINs',
    } as const;
    tabsRow.replaceChildren(
      ...(['schedule', 'groups', 'teachers'] as const).map((name) =>
        h(
          'button',
          {
            class: 'vad-tab',
            type: 'button',
            role: 'tab',
            'aria-selected': String(tab === name),
            onclick: () => {
              tab = name;
              scheduleRender();
            },
          },
          TAB_LABEL[name],
        ),
      ),
    );

    if (tab === 'schedule') {
      main.className = 'vad-main two';
      editorCol.replaceChildren(renderScopeCard(), renderDayCard(), renderBellsCard());
      sideCol.style.display = '';
      sideCol.replaceChildren(renderPreview());
    } else {
      main.className = 'vad-main';
      editorCol.replaceChildren(tab === 'groups' ? renderGroups() : renderTeachers());
      sideCol.style.display = 'none';
      sideCol.replaceChildren();
    }

    const count = describeChanges().length;
    const pending = dirty();
    bar.replaceChildren(
      h(
        'div',
        { class: 'count' },
        h(
          'b',
          {},
          !pending
            ? 'Nothing to publish'
            : count
              ? `${count} unpublished change${count === 1 ? '' : 's'}`
              : 'Unpublished changes',
        ),
        flash
          ? h('span', { class: `vad-flash ${flash.kind}` }, flash.text)
          : h(
              'span',
              {},
              `Published v${store.doc.version} · ${groupName(draft, group)} · ${scope === 'date' ? shortDate(date) : 'everyday'}`,
            ),
      ),
      h('span', { class: 'vad-spacer' }),
      h(
        'button',
        {
          class: 'vad-btn',
          type: 'button',
          disabled: !pending,
          onclick: () => {
            void ask({
              title: 'Throw away your edits?',
              body: 'Everything goes back to what is published right now.',
              confirm: 'Throw them away',
            }).then((yes) => {
              if (!yes) return;
              draft = clone(store.doc);
              baseline = clone(store.doc);
              // The teachers those PINs were typed for are going away with the
              // draft; a PIN with nobody to attach to must not linger.
              pinAfterPublish.clear();
              pinNote.clear();
              flash = null;
              render();
            });
          },
        },
        'Discard',
      ),
      h('button', { class: 'vad-btn primary', type: 'button', disabled: !pending, onclick: openPublish }, 'Review & publish'),
    );

    if (fkey) {
      const again = shell.querySelector<HTMLElement>(`[data-fkey="${CSS_ESCAPE(fkey)}"]`);
      if (again) {
        again.focus();
        if (again instanceof HTMLInputElement && caret !== null && again.type !== 'date') {
          try {
            again.setSelectionRange(caret, caret);
          } catch {
            // Number inputs refuse selection ranges; harmless.
          }
        }
      }
    }
  }

  render();
  void loadPinState();

  // ------------------------------------------------------------------ unmount
  return (): void => {
    window.removeEventListener('beforeunload', guard);
    root.replaceChildren();
  };
}

/** `CSS.escape` with a fallback for the odd kiosk browser that lacks it. */
function CSS_ESCAPE(value: string): string {
  const api = (globalThis as { CSS?: { escape?: (v: string) => string } }).CSS;
  if (api?.escape) return api.escape(value);
  return value.replace(/["\\]/g, '\\$&');
}
